<template>
  <Base>
    <template v-slot:toolbar>
      <div id="kt_toolbar" class="toolbar">
        <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
          <div class="d-flex align-items-center me-3">
            <slot name="leftToolbar">
              <span class="h-20px border-gray-200 border-start mx-4"></span>
              <ul class="breadcrumb breadcrumb-separatorless fw-bold fs-7 my-1">
                <li class="breadcrumb-item text-muted">
                  <router-link :to="{ name: 'dashboard' }" class="text-muted text-hover-primary">
                    <i class="fad fa-home"></i>
                    Tableau de bord
                  </router-link>
                </li>
              </ul>
            </slot>
          </div>


          <div class="d-flex align-items-center py-1">
            <label class="btn btn-warning btn-sm ms-3">
              <i class="fad fa-download"></i>
              {{ trans('Importer') }}
              <input accept=".devis,.json" class="hidden" type="file" @change="loadJsonData">
            </label>
            <label class="btn btn-info btn-sm ms-3" @click="downloadData">
              <i class="fad fa-upload"></i>
              {{ trans('Exporter') }}
            </label>
          </div>

        </div>
      </div>
    </template>

    <template #content>

      <!-- WIZARD NAV -->
      <div class="mx-5 mb-5">
        <div class="d-flex align-items-center justify-content-between">
          <div class="fs-2x fw-bolder text-primary text-uppercase">
            {{ $store.state.contractStore.avenantType?.label }} ❖ {{ $store.state.contractStore.branchCategory?.label }} {{ $store.state.contractStore.branch?.label }}
          </div>
          <div class="d-flex align-items-center" style="gap:10px">
            <div v-if="engineStatus === 'loading'" class="btn btn-sm btn-light-primary pe-none"><b-spinner small class="me-2"/>Moteur...</div>
            <div v-else-if="engineStatus === 'ready'" class="btn btn-sm btn-light-success pe-none"><i class="fas fa-check-circle me-2"></i>Moteur</div>
            <div v-else-if="engineStatus === 'error'" class="btn btn-sm btn-light-danger cursor-pointer" @click="loadEngine"><i class="fas fa-exclamation-circle me-2"></i>Réessayer</div>
            <button class="btn btn-icon btn-sm btn-light-dark" @click="showDebug = !showDebug" title="Debug moteur"><i class="fad fa-bug"></i></button>
          </div>
        </div>
        <div class="d-flex mt-5 border-bottom pb-3" style="gap:5px">
          <div v-for="step in visibleSteps" :key="'ws_'+step.n"
               class="d-flex align-items-center cursor-pointer px-4 py-2 rounded"
               :class="currentStep === step.n ? 'bg-primary text-white' : (step.n < currentStep ? 'bg-light-success text-success' : 'bg-light text-muted')"
               @click="goToStep(step.n)">
            <i :class="step.icon + ' me-2'"></i>
            <span class="fw-bolder fs-7">{{ step.label }}</span>
            <i v-if="step.n < currentStep" class="fas fa-check-circle ms-2"></i>
          </div>
        </div>
      </div>

      <!-- STEP 1 : Configuration + Comparaison -->
      <template v-if="currentStep === 1">
        <div class="mx-5 my-5 d-flex justify-content-start align-items-center" style="gap:20px">
          <div class="d-flex align-items-center fw-bolder fs-2">Rechercher un objet :</div>
          <div>
            <select v-model="itemSearch.item" class="form form-control form-select">
              <option value="">Type d'objet</option>
              <template v-for="(it,key) in $store.state.contractStore.itemTypes">
                <option v-if="!it.required && !it.isRoot && canAddItemType(it)" :key="'searched_item'+key" :value="it.code">{{ it.name }}</option>
              </template>
            </select>
          </div>
          <div>
            <select v-model="itemSearch.property" class="form form-control form-select">
              <option value="">Propriété</option>
              <template v-for="(prop,key) in searchProperties">
                <option :key="'searched_prop'+prop.uuid" :value="prop.uuid">{{ prop.name }}</option>
              </template>
            </select>
          </div>
          <div><input v-model="itemSearch.value" class="form form-control" placeholder="Valeur" type="text"></div>
          <div><div class="btn btn-primary btn-sm"><i class="fad fa-search"></i> Rechercher</div></div>
        </div>
        <div class="d-flex justify-content-between" style="gap:40px">
          <template v-for="item in requiredItems">
            <div :key="'required_'+item.uuid" :class="{'border-danger': !item.isValid,'border-success':item.isValid}" class="card flex-1 border">
              <div class="card-body p-5">
                <div class="fs-2 fw-bolder text-primary text-uppercase">{{ item.name }}</div>
                <div class="row">
                  <template v-for="(pm, key) in getItemTypeProperties(item.modelCode)">
                    <AvenantPropertyManager v-if="pm.type && !pm.type.code.match(/OBJECT/) && !pm.internal"
                      :key="'prop_'+item.uuid+'_'+pm.code" :itemUuid="item.uuid" :propertyCode="pm.code"
                      :type="pm.type" class="col col-4" @propertyUpdated="updateItems([item.uuid])"/>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="card mt-10">
          <div class="card-body p-20 position-relative">
            <AvenantItemTree v-if="$store.state.contractStore.currentItem" :key="'parent_'+$store.state.contractStore.currentItem" :maxDepth="5" :uuid="$store.state.contractStore.currentItem" @expand="expand" @itemUpdated="updateItems" @removeItem="onItemRemoved" @link="prepareChildrenLink"/>
          </div>
        </div>

        <div class="card mt-10">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-5">
              <h2 class="mb-0"><i class="fad fa-balance-scale me-2"></i>Comparaison des assureurs</h2>
              <button :disabled="comparing" class="btn btn-primary" @click="compareAllInsurers">
                <b-spinner v-if="comparing" small class="me-2"/>
                <i v-else class="fad fa-calculator me-2"></i>
                {{ comparing ? 'Calcul en cours...' : 'Comparer' }}
              </button>
            </div>
            <div v-if="comparing" class="text-center py-10">
              <b-spinner/><div class="text-muted mt-3">Calcul en cours...</div>
            </div>
            <div v-else-if="comparisonResults.length > 0" class="w-100 offers">
              <div class="bg-success fs-2 d-flex justify-content-between align-items-center px-5 py-3 text-white fw-bolder rounded mb-5">
                <div>{{ validComparisonResults.length }} offres disponibles</div>
                <div class="btn btn-xs btn-icon btn-light-primary" @click="showAllComparisonDebug"><i class="fad fa-cogs"></i></div>
              </div>
              <div class="comparison-scroll-wrapper overflow-auto">
                <table class="comparison-table w-100">
                  <thead>
                    <tr>
                      <th class="comparison-label-col p-2" style="vertical-align:bottom">
                        <div class="fw-bolder">Garanties</div>
                      </th>
                      <template v-for="(cr, idx) in validComparisonResults">
                        <th :key="'crh_'+idx" class="text-center px-3" style="min-width:200px">
                          <div class="d-flex flex-column align-items-center px-3 py-3">
                            <div class="pb-2 fw-bolder" style="white-space:nowrap;height:50px">
                              <img v-if="cr.insurer.photo" :src="uploadRoot + cr.insurer.photo" :alt="cr.insurer.label" class="h-50px">
                              <template v-else>{{ cr.insurer.label }}</template>
                            </div>
                            <div class="fw-bolder fs-8 text-uppercase mt-1">{{ cr.insurer.label }}</div>
                            <div v-if="cr.reductionAmount" class="fs-2 text-danger text-decoration-line-through fw-bolder mt-2" style="white-space:nowrap">{{ currency(cr.premium + cr.reductionAmount) }}</div>
                            <div class="fs-2x text-primary fw-bolder" :class="{'mt-2': !cr.reductionAmount}" style="white-space:nowrap">{{ currency(cr.premium) }}</div>
                            <div class="mt-3 d-flex" style="gap:5px">
                              <div class="btn btn-sm btn-warning" @click="selectInsurer(cr)">Souscrire</div>
                              <div class="btn btn-sm btn-icon btn-light-dark" @click="showComparisonDebug(cr)"><i class="fad fa-bug"></i></div>
                            </div>
                          </div>
                        </th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Lignes de garanties -->
                    <tr v-for="g in comparisonGuarantees" :key="'gr_'+g.id">
                      <td class="comparison-label-col p-2" style="white-space:nowrap">
                        <div style="width:17rem;white-space:initial">{{ g.label }}</div>
                      </td>
                      <template v-for="(cr, idx) in validComparisonResults">
                        <td :key="'gc_'+g.id+'_'+idx" class="p-2 text-center" valign="middle">
                          <template v-if="getGuaranteeStatus(cr, g.id) === 'included'">
                            <i class="fad fa-shield-check text-success fs-2"></i>
                          </template>
                          <template v-else-if="getGuaranteeStatus(cr, g.id) === 'optional'">
                            <i class="fad fa-shield text-muted fs-2" title="Optionnelle"></i>
                          </template>
                          <template v-else>
                            <i class="fad fa-times-circle fs-2 text-danger" title="Non couverte"></i>
                          </template>
                        </td>
                      </template>
                    </tr>
                    <!-- Séparateur détails prime -->
                    <tr v-if="comparisonDetailRows.length > 0">
                      <td class="comparison-label-col p-2 fw-bolder text-muted border-top">Détails prime</td>
                      <td v-for="(cr, idx) in validComparisonResults" :key="'sep_'+idx" class="border-top p-2"></td>
                    </tr>
                    <!-- Lignes de détails prime -->
                    <tr v-for="(code, didx) in comparisonDetailRows" :key="'crd_'+didx">
                      <td class="comparison-label-col p-2" style="white-space:nowrap">{{ getComparisonDetailName(code) }}</td>
                      <template v-for="(cr, idx) in validComparisonResults">
                        <td :key="'crd_'+didx+'_'+idx" class="text-end px-5 py-2">
                          <span v-if="getComparisonDetail(cr, code)" class="fw-bolder">{{ currency(getComparisonDetail(cr, code).value) }}</span>
                          <span v-else class="text-muted">-</span>
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="text-center py-10 text-muted">
              <i class="fad fa-balance-scale fs-3x mb-3 d-block text-gray-300"></i>
              Cliquez sur Comparer pour lancer la comparaison
            </div>
          </div>
        </div>
      </template>

      <!-- STEP 3 : Garanties optionnelles -->
      <template v-if="currentStep === 3">
        <div class="card">
          <div class="card-body">
            <div v-if="$store.state.contractStore.insurer" class="d-flex align-items-center mb-5 pb-5 border-bottom">
              <img v-if="$store.state.contractStore.insurer.photo" :src="uploadRoot + $store.state.contractStore.insurer.photo" class="h-60px me-5 rounded" :alt="$store.state.contractStore.insurer.label">
              <div>
                <div class="fw-bolder fs-2x text-primary">{{ $store.state.contractStore.insurer.label }}</div>
                <div class="fs-2 text-muted">{{ duration }} — {{ datify(start, 'dmY') }} → {{ datify(end, 'dmY') }}</div>
              </div>
              <div class="ms-auto">
                <div v-if="calculating" class="d-flex align-items-center text-muted"><b-spinner small class="me-2"/>Calcul...</div>
                <div v-else class="fs-3x fw-bolder text-primary">{{ currency(result ? (result.customPremium ?? result.premium) : 0) }}</div>
              </div>
            </div>

            <div class="guarantee-scroll-wrapper overflow-auto">
              <table class="guarantee-table">
                <thead>
                  <tr>
                    <th class="guarantee-label-col p-2 fw-bolder" style="vertical-align:bottom">Actifs</th>
                    <template v-for="g in selectedInsurerGuarantees">
                      <th :key="'gh_'+g.id" class="text-center px-3 py-2" style="min-width:120px;vertical-align:bottom">
                        <div class="fw-bolder fs-8">{{ g.label }}</div>
                        <b-dropdown v-if="g.deductible || g.ceiling" no-caret size="sm" variant="link" class="mt-1" toggle-class="p-0 text-decoration-none">
                          <template #button-content>
                            <i class="fad fa-info-circle text-muted cursor-pointer fs-7"></i>
                          </template>
                          <b-dropdown-text style="min-width:200px">
                            <div v-if="g.deductible" class="mb-2"><span class="fw-bolder">Franchise :</span> <span v-html="g.deductible"></span></div>
                            <div v-if="g.ceiling"><span class="fw-bolder">Plafond :</span> <span v-html="g.ceiling"></span></div>
                          </b-dropdown-text>
                        </b-dropdown>
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ac in assetCoverages" :key="'acr_'+ac.assetUuid">
                    <td class="guarantee-label-col p-2 fw-bolder" style="white-space:nowrap">
                      {{ ac.assetName }}
                      <div class="text-muted fs-9 fw-normal">{{ ac.packLabel }}</div>
                    </td>
                    <template v-for="g in selectedInsurerGuarantees">
                      <td :key="'gc_'+ac.assetUuid+'_'+g.id" class="p-2 text-center" valign="middle">
                        <template v-if="getAssetGuaranteeInfo(ac, g.id)">
                          <!-- Incluse -->
                          <template v-if="!getAssetGuaranteeInfo(ac, g.id).isOptional">
                            <i class="fad fa-shield-check text-success fs-2"></i>
                          </template>
                          <!-- Optionnelle active -->
                          <template v-else-if="getAssetGuaranteeInfo(ac, g.id).active">
                            <div class="d-flex align-items-center justify-content-center position-relative">
                              <i class="fad fa-shield-check text-primary fs-2"></i>
                              <div class="px-2 cursor-pointer position-absolute btn btn-danger btn-xs btn-icon" style="right:0" @click.stop.prevent="toggleGuarantee(ac, getAssetGuaranteeInfo(ac, g.id))">
                                <i class="fad fa-times"></i>
                              </div>
                            </div>
                          </template>
                          <!-- Optionnelle disponible -->
                          <template v-else>
                            <label class="fw-bolder cursor-pointer text-primary text-decoration-underline" @click.stop.prevent="toggleGuarantee(ac, getAssetGuaranteeInfo(ac, g.id))">
                              <i class="fad fa-plus-circle text-primary"></i>
                              <span v-if="getAssetGuaranteeInfo(ac, g.id).premium">{{ currency(getAssetGuaranteeInfo(ac, g.id).premium) }}</span>
                            </label>
                          </template>
                        </template>
                        <!-- Non couverte -->
                        <template v-else>
                          <i class="fad fa-times-circle fs-2 text-danger"></i>
                        </template>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </template>

      <!-- STEP 2 : Récap des propriétés des assets -->
      <template v-if="currentStep === 2">
        <div class="card">
          <div class="card-body">
            <div v-if="$store.state.contractStore.insurer" class="d-flex align-items-center mb-5 pb-5 border-bottom">
              <img v-if="$store.state.contractStore.insurer.photo" :src="uploadRoot + $store.state.contractStore.insurer.photo" class="h-60px me-5 rounded" :alt="$store.state.contractStore.insurer.label">
              <div>
                <div class="fw-bolder fs-2x text-primary">{{ $store.state.contractStore.insurer.label }}</div>
                <div class="fs-2 text-muted">{{ duration }} — {{ datify(start, 'dmY') }} → {{ datify(end, 'dmY') }}</div>
              </div>
              <div class="ms-auto fs-3x fw-bolder text-primary">{{ currency(result ? (result.customPremium ?? result.premium) : 0) }}</div>
            </div>

            <div v-if="assetPropertiesColumns.length === 0" class="text-muted fs-7">Aucune propriété renseignée sur les actifs.</div>
            <div v-else class="guarantee-scroll-wrapper overflow-auto">
              <table class="guarantee-table">
                <thead>
                  <tr>
                    <th class="guarantee-label-col p-2 fw-bolder" style="vertical-align:bottom">Actifs</th>
                    <th class="guarantee-pack-col p-2 fw-bolder text-center" style="vertical-align:bottom">Catégorie de pack</th>
                    <template v-for="col in assetPropertiesColumns">
                      <th :key="'apc_'+col.code" class="text-center px-3 py-2" style="min-width:120px;vertical-align:bottom">
                        <div class="fw-bolder fs-8">{{ col.label }}</div>
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in assetPropertiesRecap" :key="'apr_'+row.assetUuid">
                    <td class="guarantee-label-col p-2 fw-bolder" style="white-space:nowrap">{{ row.assetName }}</td>
                    <td class="guarantee-pack-col p-2 text-center fs-7" valign="middle">
                      <span v-if="getAssetPackCategoryLabel(row.assetUuid)" class="badge badge-light-info">{{ getAssetPackCategoryLabel(row.assetUuid) }}</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <template v-for="col in assetPropertiesColumns">
                      <td :key="'apv_'+row.assetUuid+'_'+col.code" class="p-2 text-center fs-7" valign="middle">
                        <template v-if="col.type && col.type.code === 'BOOLEAN'">
                          <i v-if="row.values[col.code] === true || row.values[col.code] === 'true' || row.values[col.code] === '1'" class="fad fa-check text-success fs-3"></i>
                          <i v-else-if="row.values[col.code] === false || row.values[col.code] === 'false' || row.values[col.code] === '0'" class="fad fa-times text-danger fs-3"></i>
                          <i v-else class="fad fa-minus text-muted"></i>
                        </template>
                        <template v-else>
                          <span v-if="helper.empty(row.values[col.code])" class="text-muted">—</span>
                          <template v-else>{{ formatAssetPropertyValue(row.values[col.code], col) }}</template>
                        </template>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>

      <!-- STEP 4 : Personnalisation des détails -->
      <template v-if="currentStep === 4">
        <div class="card">
          <div class="card-body">
            <div v-if="$store.state.contractStore.insurer" class="d-flex align-items-center mb-5 pb-5 border-bottom">
              <img v-if="$store.state.contractStore.insurer.photo" :src="uploadRoot + $store.state.contractStore.insurer.photo" class="h-60px me-5 rounded" :alt="$store.state.contractStore.insurer.label">
              <div>
                <div class="fw-bolder fs-2x text-primary">{{ $store.state.contractStore.insurer.label }}</div>
                <div class="fs-2 text-muted">{{ duration }} — {{ datify(start, 'dmY') }} → {{ datify(end, 'dmY') }}</div>
              </div>
              <div class="ms-auto fs-3x fw-bolder text-primary">{{ currency(result ? (result.customPremium ?? result.premium) : 0) }}</div>
            </div>

            <div class="fw-bolder fs-2 mb-5">Détails de facture</div>
            <table class="table g-2 table-head-custom table-head-bg table-borderless table-vertical-center table-hover table-striped">
              <thead><tr><th>Label</th><th class="text-end">Montant</th></tr></thead>
              <tbody>
              <tr v-for="premiumDetail in premiumDetails">
                <td>
                  <div class="fw-bolder">{{ premiumDetail.model.label }}</div>
                  <div v-if="premiumDetail.isCustomized" class="fs-9 text-info">
                    <i class="fad fa-user-edit me-1"></i>Modifié par {{ premiumDetail.modifiedBy }}
                    <span v-if="premiumDetail.modifiedAt" class="text-muted">le {{ premiumDetail.modifiedAt }}</span>
                    <span class="text-muted ms-1">(moteur: {{ currency(premiumDetail.originalPremium) }})</span>
                  </div>
                </td>
                <td class="text-end">
                  <div class="d-flex justify-content-end align-items-center">
                    <template v-if="!premiumDetail.edit">
                      <span class="fw-bolder" :class="premiumDetail.isCustomized ? 'text-info' : ''">
                        {{ currency(premiumDetail.isCustomized ? premiumDetail.customPremium : premiumDetail.premium) }}
                      </span>
                      <div class="btn btn-icon btn-xs btn-light-primary ms-3" @click="startEditPremiumDetail(premiumDetail)"><i class="fad fa-edit"></i></div>
                    </template>
                    <template v-else>
                      <input v-model.number="premiumDetail.customPremium" class="form-control form-control-sm text-end" type="number" style="width:140px">
                      <div class="btn btn-icon btn-xs btn-success ms-2" @click="saveEditPremiumDetail(premiumDetail)"><i class="fad fa-check"></i></div>
                      <div class="btn btn-icon btn-xs btn-light-danger ms-1" @click="cancelEditPremiumDetail(premiumDetail)"><i class="fad fa-times"></i></div>
                    </template>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>

            <div class="fw-bolder fs-2 mb-5 mt-10">Commissions</div>
            <table class="table g-2 table-head-custom text-center table-head-bg table-borderless table-vertical-center table-hover table-striped">
              <thead><tr><th>Label</th><th>Montant</th></tr></thead>
              <tbody>
              <tr v-for="commission in commissions">
                <td>{{ commission.model.label }}</td>
                <td><input v-model="commission.premium" class="form-control text-end" type="number"></td>
              </tr>
              </tbody>
            </table>

            <div class="fw-bolder fs-2 mb-5 mt-10">Réductions</div>
            <table class="table g-2 table-head-custom text-center table-head-bg table-borderless table-vertical-center table-hover table-striped">
              <thead><tr><th>Label</th><th>Montant</th></tr></thead>
              <tbody>
              <tr v-for="reduction in reductions">
                <td>{{ reduction.model.label }}</td>
                <td><input v-model="reduction.premium" class="form-control text-end" type="number"></td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <!-- STEP 5 : Échéancier -->
      <template v-if="currentStep === 5">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-5">
              <div class="fw-bolder fs-2">Échéancier</div>
              <div class="fs-2x fw-bolder text-primary">{{ currency(result ? (result.customPremium ?? result.premium) : 0) }}</div>
            </div>
            <div v-if="deadlineReste > 0" class="badge badge-light-warning fs-7 mb-5">Reste à répartir : {{ currency(deadlineReste) }}</div>
            <table v-if="deadlines.length > 0" class="table g-2 table-head-custom text-center table-head-bg table-borderless table-vertical-center table-hover table-striped">
              <thead><tr><th>Date</th><th>Montant</th><th></th></tr></thead>
              <tbody>
              <tr v-for="(deadline,index) in deadlines">
                <td><input v-model="deadline.date" class="form form-control" type="date"></td>
                <td><input v-model="deadline.premium" class="form form-control" type="number"></td>
                <td><div class="btn btn-danger btn-icon btn-xs" @click="removeDeadline(index)"><i class="fad fa-times"></i></div></td>
              </tr>
              </tbody>
            </table>
            <div class="d-flex justify-content-center py-5">
              <div class="btn btn-sm btn-primary" @click="addDeadline"><i class="fad fa-plus me-2"></i>Ajouter une échéance</div>
            </div>
          </div>
        </div>
      </template>

      <!-- STEP 6 : Récapitulatif (style identique au template Twig devis.html.twig) -->
      <template v-if="currentStep === 6">
        <div class="devis-actions no-print">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <button class="btn btn-light-primary" @click="goPrev">
                <i class="fad fa-arrow-left me-2"></i>Précédent
              </button>
            </div>
            <div class="fw-bolder fs-2">
              <span class="text-primary">{{ currency(result ? (result.customPremium ?? result.premium) : 0) }}</span>
              <span class="text-muted ms-3 fs-4">{{ $store.state.contractStore.insurer?.label }}</span>
            </div>
            <div class="d-flex" style="gap:10px">
              <button class="btn btn-info" @click="printRecap">
                <i class="fad fa-file-pdf me-2"></i>Télécharger PDF
              </button>
              <button class="btn btn-success" @click="generateAvenant">
                <i class="fad fa-file-signature me-2"></i>Créer le contrat
              </button>
            </div>
          </div>
        </div>
        <div id="devis-recap" class="devis-page">
          <!-- HEADER -->
          <table class="devis-head">
            <tbody><tr>
              <td style="text-align:left">
                <img v-if="$store.state.contractStore.insurer?.photo" :src="uploadRoot + $store.state.contractStore.insurer.photo" style="height:80px">
              </td>
              <td style="line-height:1.5em;text-align:right">
                <div><strong>Souscripteur:</strong></div>
                <template v-for="item in requiredItems">
                  <template v-if="getItemModel(item)?.code === 'souscripteur'">
                    <div><strong style="text-transform:uppercase">{{ item.properties.noms }} {{ item.properties.prenoms }}</strong></div>
                    <div v-if="item.properties.email">{{ item.properties.email }}</div>
                    <div v-if="item.properties.contact">{{ item.properties.contact }}</div>
                  </template>
                </template>
              </td>
            </tr></tbody>
          </table>

          <!-- TITRE -->
          <table style="padding:2rem 0;width:100%">
            <tr>
              <td style="text-align:left">
                <div style="font-size:2em;font-weight:700">
                  <div style="text-transform:uppercase">Devis {{ $store.state.contractStore.branch?.label }}</div>
                  <div style="color:#3ddad7">{{ $store.state.contractStore.insurer?.label }}</div>
                </div>
              </td>
              <td style="text-align:right">
                <div><strong>{{ datify(start, 'dmY') }} → {{ datify(end, 'dmY') }}</strong></div>
                <div style="text-transform:uppercase">{{ duration }}</div>
              </td>
            </tr>
          </table>

          <!-- TABLEAU DES ACTIFS -->
          <table class="devis-table devis-table-striped" style="width:100%;border-collapse:collapse;margin-bottom:2rem">
            <thead>
              <tr style="background:#3ddad7;color:white">
                <th style="text-align:left;padding:8px 10px">#</th>
                <th style="padding:8px 10px">Actifs</th>
                <th style="padding:8px 10px">Type</th>
                <th style="padding:8px 10px">Opération</th>
                <th style="padding:8px 10px">Formule</th>
                <th style="text-align:right;padding:8px 10px">Prime</th>
              </tr>
            </thead>
            <tbody>
              <!-- Affichage arbre complet (Customer + Asset) — désactivé temporairement, peut être réactivé -->
              <!-- <RecapItemRow v-if="$store.state.contractStore.rootUuid" :uuid="$store.state.contractStore.rootUuid" :level="0"/> -->
              <tr v-for="(row, ridx) in recapAssetRows" :key="'recap_asset_'+row.uuid">
                <td style="padding:8px 10px;font-weight:600">{{ ridx + 1 }}</td>
                <td style="padding:8px 10px;font-weight:600">{{ row.identifier }}</td>
                <td style="padding:8px 10px">{{ row.typeLabel }}</td>
                <td style="padding:8px 10px;text-transform:uppercase">{{ row.opLabel }}</td>
                <td style="padding:8px 10px;text-transform:uppercase">{{ row.covLabel }}</td>
                <td style="padding:8px 10px;text-align:right;font-weight:600">{{ row.premium }}</td>
              </tr>
            </tbody>
          </table>
          <div style="text-align:right;margin-bottom:1rem">
            <div class="btn btn-sm btn-light-primary no-print" @click="showFullDetails = true">
              <i class="fad fa-list-tree me-2"></i>Voir tous les détails de calcul
            </div>
          </div>

          <!-- MODAL DETAILS COMPLETS -->
          <div v-if="showFullDetails" class="no-print" style="position:fixed;top:0;left:0;right:0;bottom:0;z-index:1000;display:flex;align-items:center;justify-content:center">
            <div style="position:absolute;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5)" @click="showFullDetails = false"></div>
            <div style="position:relative;background:white;border-radius:10px;max-width:90vw;max-height:90vh;overflow:auto;padding:2rem;min-width:600px">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:1.5rem">
                <div style="font-weight:700;font-size:1.3em;color:#f8069c">Détails de calcul complets</div>
                <div class="btn btn-icon btn-sm btn-light-danger" @click="showFullDetails = false"><i class="fad fa-times"></i></div>
              </div>
              <template v-for="(uuid, aidx) in allAssetUuids">
                <div v-if="$store.state.contractStore.items[uuid]" :key="'modal_asset_'+uuid" :set="assetCov = getAssetCoverage(uuid)" style="margin-bottom:2rem">
                  <div style="background:#3ddad7;color:white;padding:0.5em 1em;border-radius:4px;font-weight:600;margin-bottom:0.5rem">
                    {{ $store.state.contractStore.items[uuid].name }}
                    <span v-if="assetCov && assetCov.properties.label" style="margin-left:8px;opacity:0.8;font-weight:400">{{ assetCov.properties.label }}</span>
                    <span v-if="assetCov" style="float:right">{{ currency(assetCov.properties.premium) }}</span>
                  </div>
                  <div v-if="assetCov && assetCov.properties.risques && assetCov.properties.risques.length > 0" style="padding:4px 0 8px 0;display:flex;flex-wrap:wrap;gap:4px">
                    <template v-for="(risk, ri) in assetCov.properties.risques">
                      <span :key="'modal_g_'+uuid+'_'+ri"
                            :style="'display:inline-block;padding:2px 10px;border-radius:20px;font-size:0.8em;font-weight:600;color:white;background:' + (isOptionalRisk(risk, uuid) ? '#ffc107' : '#28a745')">
                        {{ risk.label }}
                      </span>
                    </template>
                  </div>
                  <template v-if="assetCov && (assetCov.details || assetCov.properties.details) && (assetCov.details || assetCov.properties.details).length > 0">
                    <table style="width:100%;border-collapse:collapse">
                      <tbody>
                      <tr v-for="(detail, di) in (assetCov.details || assetCov.properties.details)" :key="'modal_det_'+uuid+'_'+di"
                          :style="di % 2 === 0 ? 'background:#f5f5f5' : ''">
                        <td style="padding:6px 10px">{{ detail.name }}</td>
                        <td style="text-align:right;padding:6px 10px">=</td>
                        <td style="text-align:right;padding:6px 10px;font-weight:600">{{ isNumeric(detail.value) ? currency(detail.value) : detail.value }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </template>
                  <div v-else style="padding:0.5em 1em;color:#888;font-style:italic">Aucun détail disponible</div>
                </div>
              </template>

              <div style="background:#f8069c;color:white;padding:0.5em 1em;border-radius:4px;font-weight:700;font-size:1.1em;margin-top:1rem;display:flex;justify-content:space-between">
                <span>PRIME TOTALE TTC</span>
                <span>{{ currency(result ? (result.customPremium ?? result.premium) : 0) }}</span>
              </div>
            </div>
          </div>

          <!-- DETAILS DU TARIF -->
          <table class="devis-table" style="width:100%;border-collapse:collapse;margin-bottom:2rem">
            <thead>
              <tr>
                <th style="background:#3ddad7;color:white;text-align:left;padding:8px 10px">Détails du tarif</th>
                <th style="background:#3ddad7;color:white;text-align:right;padding:8px 10px">{{ $store.state.contractStore.insurer?.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="padding:0;vertical-align:top">
                  <table style="width:100%;border-collapse:collapse">
                    <tbody>
                    <template v-for="(pd, pidx) in premiumDetails">
                      <tr v-if="Math.abs(pd.isCustomized ? pd.customPremium : pd.premium) > 0" :key="'recap_pd_'+pidx">
                        <td style="padding:10px;white-space:nowrap">
                          {{ pd.model.label }}
                          <span v-if="pd.isCustomized" style="color:#3ddad7;font-size:0.8em"> (modifié)</span>
                        </td>
                        <td style="text-align:right;padding:10px">{{ pd.model.operator || '=' }}</td>
                        <td style="text-align:right;padding:10px;font-weight:600">{{ currency(pd.isCustomized ? pd.customPremium : pd.premium) }}</td>
                      </tr>
                    </template>
                    <tr style="background:#f5f5f5;font-size:1.2rem">
                      <td style="padding:5px 10px"><strong>Prime TTC</strong></td>
                      <td></td>
                      <td style="text-align:right;padding:10px"><strong>{{ currency(result ? (result.customPremium ?? result.premium) : 0) }}</strong></td>
                    </tr>
                    </tbody>
                  </table>
                </td>
                <td style="padding:0;vertical-align:top">
                  <table style="width:100%">
                    <tr>
                      <td style="padding:10px"><strong>Date d'effet</strong></td>
                      <td style="text-align:right;padding:10px">{{ datify(start, 'dmY') }}</td>
                    </tr>
                    <tr>
                      <td style="padding:10px"><strong>Durée</strong></td>
                      <td style="text-align:right;padding:10px;text-transform:uppercase">{{ duration }}</td>
                    </tr>
                    <tr>
                      <td style="padding:10px"><strong>Date d'expiration</strong></td>
                      <td style="text-align:right;padding:10px">{{ datify(end, 'dmY') }}</td>
                    </tr>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- ECHEANCIER -->
          <table v-if="deadlines.length > 0" class="devis-table" style="width:100%;border-collapse:collapse;margin-bottom:2rem">
            <thead>
              <tr style="background:#3ddad7;color:white">
                <th style="text-align:left;padding:8px 10px">Échéance</th>
                <th style="text-align:right;padding:8px 10px">Montant</th>
              </tr>
            </thead>
            <tbody>
            <tr v-for="(dl, idx) in deadlines" :key="'recap_dl_'+idx" :style="idx % 2 === 0 ? 'background:#f5f5f5' : ''">
              <td style="padding:8px 10px">{{ dl.date }}</td>
              <td style="padding:8px 10px;text-align:right;font-weight:600">{{ currency(dl.premium) }}</td>
            </tr>
            </tbody>
          </table>
          <!-- Footer page principale -->
          <div style="flex:1"></div>
          <div style="margin-top:2rem;border-top:1px solid #eee;padding-top:1rem">
            <table style="width:100%">
              <tr style="text-align:center">
                <td style="padding:5px;width:74px">
                  <img v-if="$store.state.contractStore.insurer?.photo" :src="uploadRoot + $store.state.contractStore.insurer.photo" style="max-height:50px;object-fit:contain">
                </td>
                <td style="text-align:left;padding-left:20px;font-size:0.85em;color:#888">
                  {{ $store.state.contractStore.insurer?.label }}
                </td>
                <td style="text-align:right;font-size:0.85em;color:#888">
                  {{ datify(new Date(), 'dmY') }}
                </td>
              </tr>
            </table>
          </div>
        </div>

        <!-- PAGES DETAIL PAR ACTIF -->
        <template v-for="(uuid, aidx) in allAssetUuids">
          <div v-if="$store.state.contractStore.items[uuid]" :key="'recap_detail_'+uuid"
               class="devis-page" :set="assetCov = getAssetCoverage(uuid)">
            <!-- Header mini -->
            <table class="devis-head" style="margin-bottom:1rem">
              <tbody><tr>
                <td style="text-align:left">
                  <img v-if="$store.state.contractStore.insurer?.photo" :src="uploadRoot + $store.state.contractStore.insurer.photo" style="height:50px">
                </td>
                <td style="text-align:right;font-weight:600">
                  Devis {{ $store.state.contractStore.branch?.label }} — {{ $store.state.contractStore.insurer?.label }}
                </td>
              </tr></tbody>
            </table>

            <div style="background:#f8069c;color:white;font-weight:600;font-size:1.2em;padding:0.5em 1em;margin:0 0 1rem 0;border-radius:4px">Personne ou objet assuré</div>

            <!-- Propriétés -->
            <div style="display:flex;flex-wrap:wrap;gap:5px;margin-bottom:1.5rem" class="devis-carac">
              <template v-for="(pm, key) in getItemTypeProperties($store.state.contractStore.items[uuid].modelCode)">
                <div v-if="isPropertyVisible(pm, $store.state.contractStore.items[uuid].properties[pm.code], $store.state.contractStore.items[uuid])"
                     :key="'recap_ap_'+uuid+'_'+pm.code"
                     style="background:#f5f5f5;padding:0.5rem 1rem;text-align:center;flex-grow:1;white-space:nowrap">
                  <div style="font-weight:600;text-transform:uppercase;font-size:0.9em">{{ formatPropertyValue(pm, $store.state.contractStore.items[uuid].properties[pm.code]) }}</div>
                  <div style="font-size:0.75em;color:#888">{{ pm.name }}</div>
                </div>
              </template>
            </div>

            <!-- Garanties + Premium details -->
            <div v-if="assetCov && assetCov.properties.risques && assetCov.properties.risques.length > 0">
              <div style="background:#f8069c;color:white;font-weight:600;font-size:1.2em;padding:0.5em 1em;margin:0 0 0.5rem 0;border-radius:4px">Détails de l'offre</div>
              <table style="width:100%;border-collapse:collapse">
                <thead>
                  <tr style="background:#3ddad7;color:white;text-transform:uppercase">
                    <th style="padding:0.5em;text-align:left">Vos garanties</th>
                    <th style="padding:0.5em">Franchises</th>
                    <th style="padding:0.5em">Plafonds</th>
                  </tr>
                </thead>
                <tbody>
                <tr v-for="(risk, ri) in assetCov.properties.risques" :key="'recap_risk_'+uuid+'_'+ri"
                    :style="ri % 2 === 0 ? 'background:#f5f5f5' : ''">
                  <td style="padding:0.5rem 0.75em;font-weight:600;color:#555">{{ risk.label }}</td>
                  <td style="padding:0.5rem 0.75em;color:#888">{{ risk.min || 'Néant' }}</td>
                  <td style="padding:0.5rem 0.75em;color:#888">{{ risk.max || '-' }}</td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Premium details par actif (depuis coverage.details) -->
            <div v-if="assetCov" style="margin-top:1.5rem">
              <table style="width:100%;border-collapse:collapse">
                <thead>
                  <tr>
                    <th style="background:#3ddad7;color:white;text-align:left;padding:0.5em">Détails du tarif</th>
                    <th style="background:#3ddad7;color:white;text-align:right;padding:0.5em">{{ assetCov.properties.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style="padding:0;vertical-align:top">
                      <table style="width:100%;border-collapse:collapse">
                        <tbody>
                        <template v-if="(assetCov.details || assetCov.properties.details) && (assetCov.details || assetCov.properties.details).length > 0">
                          <template v-for="(detail, di) in (assetCov.details || assetCov.properties.details)">
                            <tr v-if="detail.value && Math.abs(detail.value) > 0" :key="'adet_'+uuid+'_'+di">
                              <td style="padding:8px 10px">{{ detail.name }}</td>
                              <td style="text-align:right;padding:8px 10px">=</td>
                              <td style="text-align:right;padding:8px 10px;font-weight:600">{{ currency(detail.value) }}</td>
                            </tr>
                          </template>
                        </template>
                        <tr style="background:#f5f5f5;font-size:1.1rem">
                          <td style="padding:5px 10px" colspan="2"><strong>Prime</strong></td>
                          <td style="text-align:right;padding:8px 10px"><strong>{{ currency(assetCov.properties.premium) }}</strong></td>
                        </tr>
                        </tbody>
                      </table>
                    </td>
                    <td style="padding:0;vertical-align:top">
                      <table style="width:100%">
                        <tr><td style="padding:8px 10px"><strong>Date d'effet</strong></td><td style="text-align:right;padding:8px 10px">{{ datify(start, 'dmY') }}</td></tr>
                        <tr><td style="padding:8px 10px"><strong>Durée</strong></td><td style="text-align:right;padding:8px 10px;text-transform:uppercase">{{ duration }}</td></tr>
                        <tr><td style="padding:8px 10px"><strong>Date d'expiration</strong></td><td style="text-align:right;padding:8px 10px">{{ datify(end, 'dmY') }}</td></tr>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Footer -->
            <div style="flex:1"></div>
            <div class="devis-footer" style="margin-top:2rem;border-top:1px solid #eee;padding-top:1rem">
              <table style="width:100%">
                <tr style="text-align:center">
                  <td style="padding:5px;width:74px">
                    <img v-if="$store.state.contractStore.insurer?.photo" :src="uploadRoot + $store.state.contractStore.insurer.photo" style="max-height:50px;object-fit:contain">
                  </td>
                  <td style="text-align:left;padding-left:20px;font-size:0.85em;color:#888">
                    {{ $store.state.contractStore.insurer?.label }}
                  </td>
                  <td style="text-align:right;font-size:0.85em;color:#888">
                    {{ datify(new Date(), 'dmY') }}
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </template>
      </template>

      <!-- NAVIGATION -->
      <div class="d-flex justify-content-between mt-10 mx-5">
        <div>
          <button v-if="!isFirstStep" class="btn btn-light-primary" @click="goPrev">
            <i class="fad fa-arrow-left me-2"></i>Précédent
          </button>
        </div>
        <div class="d-flex" style="gap:10px">
          <button v-if="currentStep === lastStep" class="btn btn-info" @click="printRecap">
            <i class="fad fa-file-pdf me-2"></i>Télécharger PDF
          </button>
          <button v-if="currentStep < lastStep" class="btn btn-primary" @click="goNext" :disabled="currentStep === 1 && !$store.state.contractStore.insurer">
            Suivant<i class="fad fa-arrow-right ms-2"></i>
          </button>
          <button v-if="currentStep === lastStep" class="btn btn-success" @click="generateAvenant">
            <i class="fad fa-file-signature me-2"></i>Créer le contrat
          </button>
        </div>
      </div>

      <!-- DEBUG DRAWER (global, accessible depuis toutes les étapes) -->
      <transition name="slide-right">
        <div v-if="showDebug" class="debug-drawer">
          <div class="debug-drawer-content">
            <div class="d-flex justify-content-between align-items-center p-5 border-bottom">
              <div class="fw-bolder fs-3">Debug Moteur</div>
              <div class="btn btn-icon btn-sm btn-light-danger" @click="showDebug = false"><i class="fad fa-times"></i></div>
            </div>
            <div class="d-flex flex-grow-1 overflow-hidden">
              <div class="overflow-auto flex-grow-1 p-5">
                <div v-if="engineInput" class="mb-5">
                  <div class="btn btn-sm btn-light-primary" @click="showEngineInput = true">
                    <i class="fad fa-database me-2"></i>Données envoyées au moteur
                  </div>
                </div>
                <div v-if="debugDetails.length > 0" class="mb-5">
                  <div class="fw-bolder fs-5 mb-3">
                    <i class="fad fa-list-tree me-2"></i>Details Moteur
                    <span v-if="result" class="badge badge-light-success ms-2">{{ currency(result.premium) }}</span>
                  </div>
                  <table class="table table-sm m-0">
                    <tbody>
                    <tr v-for="(detail, idx) in flatDebugDetails" :key="'dd_'+idx"
                        class="bg-hover-light-primary"
                        :class="{'bg-light-primary': debugFunctionDetail === detail, 'cursor-pointer': detail._hasChildren}">
                      <td class="py-2" :style="'padding-left:' + (20 + (detail._depth || 0) * 16) + 'px'"
                          @click="detail._hasChildren && toggleDebugExpand(detail)">
                        <i v-if="detail._hasChildren" class="fad fa-caret-right me-2 fs-9" :class="{'fa-rotate-90 text-primary': debugExpanded.has(detail)}"></i>
                        <i v-else-if="detail._depth > 0" class="fad fa-minus me-2 fs-9 text-muted"></i>
                        <span :class="detail._depth === 0 ? 'fw-bolder' : ''" :style="detail.error ? 'color:#f1416c' : ''">{{ detail.name }}</span>
                        <span v-if="detail._parentAlias" class="text-primary ms-1">{{ detail._parentAlias }}</span>
                        <span v-if="detail.error" class="badge badge-light-danger ms-2"><i class="fad fa-triangle-exclamation me-1"></i>erreur</span>
                      </td>
                      <td class="text-end py-2 px-3" :class="detail._depth === 0 ? 'fw-bolder' : 'text-muted'" style="white-space:nowrap">
                        {{ isNumeric(detail.value) ? currency(detail.value) : '' }}
                      </td>
                      <td class="py-2 px-1" style="width:64px">
                        <div class="d-flex" style="gap:4px">
                          <div v-if="detail._depth > 0"
                               class="btn btn-icon btn-xs"
                               :class="debugFunctionDetail === detail ? 'btn-primary' : (detail.error ? 'btn-light-danger' : 'btn-light-primary')"
                               @click="debugFunctionDetail = debugFunctionDetail === detail ? null : detail">
                            <i class="fad fa-eye fs-9"></i>
                          </div>
                          <router-link v-if="methodLink(detail)" :to="methodLink(detail)" target="_blank" class="btn btn-icon btn-xs btn-light-primary" title="Ouvrir la fonction dans un nouvel onglet">
                            <i class="fad fa-arrow-up-right-from-square fs-9"></i>
                          </router-link>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="debugLogs.length > 0" class="mb-5">
                  <input id="debug-logs-accordion" name="debug-logs-accordion" class="checkbox-accordion" type="checkbox"/>
                  <label for="debug-logs-accordion" class="fw-bolder fs-5 mb-3 cursor-pointer bg-hover-light-primary py-2 px-3 rounded d-flex justify-content-between align-items-center">
                    <span><i class="fad fa-caret-right me-1"></i><i class="fad fa-caret-down me-1"></i><i class="fad fa-terminal me-2"></i>Logs</span>
                    <span class="badge badge-light-info">{{ debugLogs.length }}</span>
                  </label>
                  <div class="accordion">
                    <div class="bg-light rounded p-3" style="font-size:12px;font-family:monospace">
                      <template v-for="(log, idx) in debugLogs">
                        <div :key="'log_'+idx" class="py-2 border-bottom border-gray-300">
                          <div class="d-flex align-items-start" style="gap:8px">
                            <span :class="{'badge-light-danger': log.type === 'error','badge-light-warning': log.type === 'warn','badge-light-success': log.type === 'success','badge-light-info': log.type === 'info'}" class="badge" style="min-width:55px">{{ log.type }}</span>
                            <span class="flex-grow-1">{{ log.msg }}</span>
                            <span v-if="log.data" class="text-muted cursor-pointer" @click="$set(log, 'expanded', !log.expanded)"><i class="fad fa-chevron-down" :class="{'fa-rotate-180': log.expanded}"></i></span>
                          </div>
                          <div v-if="log.where" class="d-flex align-items-center flex-wrap ps-15 mt-1 text-muted" style="gap:8px">
                            <span>{{ log.where }}</span>
                            <router-link v-if="log.link" :to="log.link" target="_blank" class="badge badge-light-primary">
                              <i class="fad fa-arrow-up-right-from-square me-1"></i>Ouvrir la fonction
                            </router-link>
                          </div>
                        </div>
                        <pre v-if="log.expanded && log.data" :key="'logdata_'+idx" class="bg-dark text-light rounded p-2 mt-1 mb-2" style="font-size:11px;max-height:300px;overflow:auto">{{ JSON.stringify(log.data, null, 2) }}</pre>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="debugFunctionDetail" class="p-5 overflow-auto" style="border-left:1px solid #eee; max-width:560px; word-break:break-word">
                <div class="d-flex justify-content-between align-items-center mb-5">
                  <h2 class="mb-0">{{ debugFunctionDetail.name || debugFunctionDetail.methodLabel }} <span v-if="isNumeric(debugFunctionDetail.value)">🔸 {{ currency(debugFunctionDetail.value) }}</span></h2>
                  <div class="d-flex align-items-center ms-5" style="gap:8px">
                    <div class="btn btn-sm" :class="showAllCond ? 'btn-light-primary' : 'btn-light'" @click="showAllCond = !showAllCond" :title="showAllCond ? 'Afficher seulement les conditions déterminantes' : 'Afficher toutes les conditions'">
                      <i class="fad fa-filter me-1"></i>{{ showAllCond ? 'Toutes les conditions' : 'Conditions clés' }}
                    </div>
                    <router-link v-if="methodLink(debugFunctionDetail)" :to="methodLink(debugFunctionDetail)" target="_blank" class="badge badge-light-primary">
                      <i class="fad fa-arrow-up-right-from-square me-1"></i>Ouvrir la fonction
                    </router-link>
                    <div class="btn btn-danger btn-xs btn-icon" @click="debugFunctionDetail = null"><i class="fad fa-times"></i></div>
                  </div>
                </div>
                <div v-if="debugFunctionDetail.error" class="alert alert-danger d-flex flex-column mb-5" style="gap:6px">
                  <div class="fw-bolder"><i class="fad fa-triangle-exclamation me-2"></i>{{ debugFunctionDetail.error }}</div>
                  <div v-if="debugFunctionDetail.audit" class="d-flex align-items-center flex-wrap" style="gap:8px">
                    <span>{{ debugFunctionDetail.insurer }} → {{ debugFunctionDetail.audit.method }} (Ligne {{ debugFunctionDetail.audit.line }}<template v-if="debugFunctionDetail.audit.type">, {{ debugFunctionDetail.audit.type }}</template>)</span>
                  </div>
                </div>
                <div v-if="isTraceNode(debugFunctionDetail)" class="d-flex flex-column" style="gap:4px">
                  <div v-for="(line, lidx) in debugFunctionDetail.inlineDetails" :key="'tl_'+lidx"
                       class="d-flex align-items-start p-2 rounded"
                       :class="(line[0] && line[0].failed) ? 'bg-light-danger' : 'bg-light'">
                    <span class="me-2 fw-bolder" :class="(line[0] && line[0].failed) ? 'text-danger' : 'text-success'">{{ line[0] ? line[0].operation : '' }}</span>
                    <div class="d-flex flex-column" style="min-width:0">
                      <span class="text-muted" style="font-size:11px">{{ line[0] ? line[0].label : '' }}</span>
                      <code :class="(line[0] && line[0].failed) ? 'text-danger fw-bolder' : 'text-gray-800'" style="white-space:pre-wrap; word-break:break-word; font-size:12px">{{ line[0] ? line[0].code : '' }}</code>
                    </div>
                  </div>
                </div>
                <template v-else-if="debugFunctionDetail.inlineDetails && debugFunctionDetail.inlineDetails.length > 0">
                  <div v-for="(line, lidx) in debugFunctionDetail.inlineDetails" :key="'line_'+lidx" class="d-flex align-items-start py-1 px-2 rounded" :class="lidx % 2 ? 'bg-light' : ''">
                    <span v-if="line[0] && line[0].ln" class="badge badge-light-dark me-2 mt-1" style="min-width:34px">L{{ line[0].ln }}</span>
                    <div class="flex-grow-1" style="min-width:0">
                    <!-- LIGNE CONDITION (if) -->
                    <div v-if="line[0] && line[0].cond" class="d-flex flex-wrap align-items-center" style="gap:6px; row-gap:6px">
                      <!-- Mode complet : groupes (OU) encadrés + atomes (ET) -->
                      <template v-if="showAllCond">
                        <template v-for="(grp, gi) in groupCond(line)">
                          <span v-if="gi > 0" :key="'or'+lidx+'-'+gi" class="badge badge-light-dark fw-bolder">ou</span>
                          <span v-if="grp.length > 1" :key="'lb'+lidx+'-'+gi" class="fs-2 fw-bolder text-muted">[</span>
                          <template v-for="(p, pi) in grp">
                            <span v-if="pi > 0" :key="'and'+lidx+'-'+gi+'-'+pi" class="badge badge-light-info">et</span>
                            <span :key="'p'+lidx+'-'+gi+'-'+pi" class="cond-atom" :class="{flipped: p.flip}">
                              <span class="badge d-flex align-items-center" :class="condClass(p)"><span class="d-flex flex-column align-items-center text-center"><span style="font-size:10px"><i class="fad me-1" :class="condIcon(p)"></i>{{ atomExpected(p) }}</span><span class="cond-bottom" style="font-size:10px"><span class="cond-field">{{ p.cmp }} {{ p.field }}</span><span class="cond-actual">{{ p.cmp }} {{ condActual(p) }}</span></span></span><i class="fad fa-caret-down cursor-pointer" :class="{'fa-rotate-180': p.flip}" @click="$set(p, 'flip', !p.flip)" style="padding:0 6px"></i></span>
                            </span>
                          </template>
                          <span v-if="grp.length > 1" :key="'rb'+lidx+'-'+gi" class="fs-2 fw-bolder text-muted">]</span>
                        </template>
                      </template>
                      <!-- Mode compact : seulement les atomes déterminants -->
                      <template v-else>
                        <template v-for="(p, pi) in condAtoms(line)">
                          <span v-if="pi > 0 && lineResult(line) === true" :key="'cc'+pi" class="badge badge-light-info">et</span>
                          <span :key="'cp'+pi" class="cond-atom" :class="{flipped: p.flip}">
                            <span class="badge d-flex align-items-center" :class="condClass(p)"><span class="d-flex flex-column align-items-center text-center"><span style="font-size:10px"><i class="fad me-1" :class="condIcon(p)"></i>{{ atomExpected(p) }}</span><span class="cond-bottom" style="font-size:10px"><span class="cond-field">{{ p.cmp }} {{ p.field }}</span><span class="cond-actual">{{ p.cmp }} {{ condActual(p) }}</span></span></span><i class="fad fa-caret-down cursor-pointer" :class="{'fa-rotate-180': p.flip}" @click="$set(p, 'flip', !p.flip)" style="padding:0 6px"></i></span>
                          </span>
                        </template>
                      </template>
                    </div>
                    <!-- LIGNE NORMALE (formule) -->
                    <div v-else class="d-flex flex-wrap align-items-center" style="gap:4px">
                      <template v-for="(place, pidx) in line">
                        <div v-if="place.operation" :key="'o'+pidx" class="badge" :class="place.failed ? 'badge-danger' : 'badge-light-info'">{{ place.operation }}</div>
                        <div :key="'pp'+pidx" class="d-flex flex-column justify-content-center align-items-center position-relative">
                          <div v-if="place.expression" class="badge badge-secondary" style="white-space:normal; word-break:break-word; max-width:100%">{{ place.expression }}</div>
                          <div v-if="place.sub" class="d-flex flex-wrap">
                            <div v-for="(sub, sidx) in place.sub" :key="'sub_'+sidx" class="d-flex flex-column justify-content-center align-items-center">
                              <div class="d-flex justify-content-center">
                                <div v-if="sub.operation" class="mx-2 badge badge-light-info">{{ sub.operation }}</div>
                                <div class="expression-wrapper cursor-pointer">
                                  <div :class="{'badge-light-success': sub.value === 'Vrai', 'badge-light-danger': sub.value === 'Faux'}" :title="sub.value" class="badge expression1 w-100">{{ sub.expression }}</div>
                                  <div v-if="sub.expression2" :class="{'badge-light-success': sub.value === 'Vrai', 'badge-light-danger': sub.value === 'Faux'}" :title="sub.value" class="badge expression2 w-100">{{ sub.expression2 }}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="isNumeric(place.value)" class="badge badge-light-success position-absolute" style="top:calc(100% + 5px)">{{ currency(place.value, false, false) }}</div>
                        </div>
                      </template>
                    </div>
                    </div>
                  </div>
                </template>
                <div v-if="(!debugFunctionDetail.inlineDetails || !debugFunctionDetail.inlineDetails.length) && !debugFunctionDetail.error" class="text-muted">
                  Aucune ligne tracée pour cette fonction — valeur : {{ isNumeric(debugFunctionDetail.value) ? currency(debugFunctionDetail.value) : debugFunctionDetail.value }}.
                </div>
              </div>
            </div>
          </div>
          <div class="debug-drawer-overlay" @click="showDebug = false"></div>
        </div>
      </transition>

      <!-- DRAWER DONNÉES ENVOYÉES AU MOTEUR (JSON) -->
      <transition name="slide-right">
        <div v-if="showEngineInput" class="debug-drawer">
          <div class="debug-drawer-content" style="width:720px">
            <div class="d-flex justify-content-between align-items-center p-5 border-bottom">
              <div class="fw-bolder fs-3"><i class="fad fa-database me-2"></i>Données envoyées au moteur</div>
              <div class="btn btn-icon btn-sm btn-light-danger" @click="showEngineInput = false"><i class="fad fa-times"></i></div>
            </div>
            <div class="d-flex flex-grow-1 overflow-hidden">
              <div class="overflow-auto flex-grow-1 p-5">
                <engine-input-debugger v-if="engineInput" :value="engineInput"></engine-input-debugger>
              </div>
            </div>
          </div>
          <div class="debug-drawer-overlay" @click="showEngineInput = false"></div>
        </div>
      </transition>

      <div v-if="showBypassModal" class="bypass-modal-overlay" @click.self="cancelBypass">
        <div class="bypass-modal card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h3 class="mb-0"><i class="fad fa-shield-exclamation text-warning me-2"></i>Propriétés invalides — Forcer la création ?</h3>
            <button class="btn btn-icon btn-sm btn-light" @click="cancelBypass"><i class="fad fa-times"></i></button>
          </div>
          <div class="card-body">
            <p class="text-muted">
              En tant que manager, vous pouvez forcer la création du contrat en validant chaque propriété non conforme.
              La responsabilité de ces dérogations vous est associée et est tracée.
            </p>
            <div v-for="err in bypassableErrors" :key="err.itemUuid + '|' + err.propertyCode" class="border rounded p-3 mb-3">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <div class="fw-bold">
                    {{ err.itemTypeName }}<span v-if="err.itemLabel"> — {{ err.itemLabel }}</span>
                  </div>
                  <div class="text-muted small">{{ err.propertyName }}</div>
                  <div class="text-danger small mt-1"><i class="fad fa-exclamation-triangle me-1"></i>{{ err.reason }}</div>
                </div>
                <label class="form-check form-switch ms-3">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="bypassSelections[err.itemUuid + '|' + err.propertyCode]"
                    @change="$set(bypassSelections, err.itemUuid + '|' + err.propertyCode, $event.target.checked)"
                  />
                  <span class="form-check-label">Forcer</span>
                </label>
              </div>
              <input
                v-if="bypassSelections[err.itemUuid + '|' + err.propertyCode]"
                type="text"
                class="form-control form-control-sm mt-2"
                placeholder="Raison (optionnelle)"
                :value="bypassReasons[err.itemUuid + '|' + err.propertyCode]"
                @input="$set(bypassReasons, err.itemUuid + '|' + err.propertyCode, $event.target.value)"
              />
            </div>
          </div>
          <div class="card-footer d-flex justify-content-end" style="gap:8px">
            <button class="btn btn-light" @click="cancelBypass">Annuler</button>
            <button class="btn btn-warning" @click="confirmBypass"><i class="fad fa-unlock me-1"></i>Relancer avec dérogations</button>
          </div>
        </div>
      </div>

    </template>
  </Base>
</template>

<script lang="ts">
import {Component, Vue, Watch} from 'vue-property-decorator';
import Base from "@/layouts/Base.vue";
import {api} from "@/services/Api";
import store from "@/store";
import TreeNode from "@/views/Income/Avenant/Components/TreeNode.vue";
import AvenantItemTree from "@/views/Income/Avenant/AvenantItemTree.vue";
import EngineInputDebugger from "@/views/Income/Avenant/Components/EngineInputDebugger.vue";
import {initItem, addPropertyFunctions, createItemFromType, createPropertyFromType, getIdentifier, getItemModel, validateItem, checkItemPropertiesVisibilities, getPropertyVisibility} from "@/services/ItemFunction";
import AvenantPropertyManager from "@/views/Income/Avenant/Components/AvenantPropertyManager.vue";
import RecapItemRow from "@/views/Income/Avenant/Components/RecapItemRow.vue";
import Popup from "@/entity/Popup";
import {helper} from "@/services/Helper";
import PropertyChoice from "@/entity/PropertyChoice";
import Duration from "@/entity/Duration";
import PremiumDetail from "@/entity/PremiumDetail";
import CommissionModel from "@/entity/CommissionModel";
import Commission from "@/entity/Commission";
import ReductionModel from "@/entity/ReductionModel";
import Reduction from "@/entity/Reduction";
import Swal from "sweetalert2";
import {engineService} from "@/services/calculator/EngineService";
import {auth} from "@/services/Auth";

@Component({
  components: {AvenantPropertyManager, AvenantItemTree, TreeNode, Base, RecapItemRow, EngineInputDebugger}
})
export default class AvenantGenerator extends Vue {

  items = this.$store.state.contractStore.items
  itemTypes = this.$store.state.contractStore.itemTypes
  uploadRoot = api.uploadRoot
  duration = null
  durationCode = null
  start: any = null
  end: any = null
  dayCount = null

  calculating = false
  engineStatus: 'idle' | 'loading' | 'ready' | 'error' = 'idle'
  branchId: number | null = null

  result: any = {
    entities: [],
    branch: '',
    insurer: '',
    premium: 0,
    customPremium: 0,
    details: [],
    deadlines: [],
    commissions: [],
    reductions: [],
  }
  contractUuid = helper.generateId()
  avenantuuid = helper.generateId()
  itemSearch = {
    item: '',
    property: '',
    value: ''
  }
  toBeValidated: any[] = []
  calculateTimeout: any = null
  premiumDetails: PremiumDetail[] = []
  commissions: Commission[] = []
  reductions: Reduction[] = []
  commissionMap: any = {}
  deadlines: any[] = []
  deadlineReste = 0
  durations: Duration[] = []
  linking: any
  calculationTime = ''
  calculationStart = 0
  calculationEnd = 0
  currentStep = 1
  wizardSteps = [
    {n: 1, label: 'Configuration', icon: 'fad fa-cogs'},
    {n: 2, label: 'Propriétés', icon: 'fad fa-list-alt'},
    {n: 3, label: 'Garanties', icon: 'fad fa-shield-check'},
    {n: 4, label: 'Détails', icon: 'fad fa-file-invoice'},
    {n: 5, label: 'Échéancier', icon: 'fad fa-calendar-alt'},
    {n: 6, label: 'Récapitulatif', icon: 'fad fa-file-check'},
  ]
  showDebug = false
  showFullDetails = false
  comparing = false
  comparisonResults: any[] = []
  selectedOptionalPremiums: any = {}
  debugLogs: any[] = []
  debugDetails: any[] = []
  debugFunctionDetail: any = null
  debugExpanded: Set<any> = new Set()
  engineInput: any = null // snapshot { insurers, items, payload } envoyé au moteur (viewer JSON)
  showEngineInput = false
  showAllCond = false // false = seulement les conditions déterminantes, true = toutes

  bypassableErrors: any[] = []
  bypassSelections: Record<string, boolean> = {}
  bypassReasons: Record<string, string> = {}
  showBypassModal = false
  lastOrder: any = null

  get allAssetUuids(): string[] {
    const storeItems = this.$store.state.contractStore.items
    const itemTypes = this.$store.state.contractStore.itemTypes
    const uuids: string[] = []
    for (const [uuid, item] of Object.entries(storeItems) as any[]) {
      const model = itemTypes[item.modelCode]
      if (model?.className === 'Asset') uuids.push(uuid)
    }
    return uuids
  }

  get recapAssetRows(): Array<{uuid: string, identifier: string, typeLabel: string, opLabel: string, covLabel: string, premium: string}> {
    const items = this.$store.state.contractStore.items
    const itemTypes = this.$store.state.contractStore.itemTypes
    const rows: any[] = []
    for (const uuid of this.allAssetUuids) {
      const item = items[uuid]
      if (!item) continue
      const model = itemTypes[item.modelCode]
      if (!model) continue
      const covUuids = item.properties?.couverture
      const covUuid = Array.isArray(covUuids) ? covUuids[0] : covUuids
      const cov = covUuid ? items[covUuid] : null
      const opCode = item.properties?.operation
      let opLabel = ''
      if (opCode) {
        const props = Array.isArray(model.properties) ? model.properties : Object.values(model.properties || {})
        const opProp: any = props.find((p: any) => p.code === 'operation')
        const choice: any = (opProp?.choices || []).find((c: any) => c.code === opCode)
        opLabel = choice?.label || opCode
      }
      rows.push({
        uuid,
        identifier: getIdentifier(item) || item.name,
        typeLabel: model.name || item.modelCode,
        opLabel: opLabel || '-',
        covLabel: cov?.properties?.label || '-',
        premium: cov?.properties?.premium ? helper.currencyFormat(cov.properties.premium) : '-',
      })
    }
    return rows
  }

  get validComparisonResults(): any[] {
    return this.comparisonResults.filter((cr: any) => !cr.error && cr.premium > 0)
  }

  get comparisonGuarantees(): any[] {
    const allGuarantees: any[] = Object.values(this.$store.state.contractStore.guarantees || {})
    // Ne garder que celles présentes dans au moins un pack d'un assureur valide
    const packs = this.$store.state.contractStore.packs || []
    const validInsurers = this.validComparisonResults.map((cr: any) => cr.insurer)
    const coveredIds = new Set<number>()
    for (const ins of validInsurers) {
      const insurerPacks = packs.filter((p: any) =>
        p.insurer?.slug === ins.slug || String(p.insurer?.id) === String(ins.id)
      )
      for (const p of insurerPacks) {
        for (const pg of (p.packGuarantees || [])) {
          if (pg.guarantee?.id) coveredIds.add(pg.guarantee.id)
        }
      }
    }
    return allGuarantees.filter((g: any) => coveredIds.has(g.id))
  }

  // Récap des propriétés des assets pour l'étape 2 : lignes = assets, colonnes = propriétés non-internal et non-OBJECT
  get assetPropertiesRecap(): {assetUuid: string, assetName: string, values: Record<string, any>}[] {
    const storeItems = this.$store.state.contractStore.items
    const itemTypes = this.$store.state.contractStore.itemTypes
    const result: any[] = []
    for (const assetUuid of this.allAssetUuids) {
      const asset = storeItems[assetUuid]
      if (!asset) continue
      const values: Record<string, any> = {}
      for (const [code, v] of Object.entries(asset.properties || {})) {
        values[code] = v
      }
      result.push({assetUuid, assetName: getIdentifier(asset) || asset.name, values})
    }
    return result
  }

  get assetPropertiesColumns(): {code: string, label: string, type: any, choices: any[]}[] {
    const storeItems = this.$store.state.contractStore.items
    const itemTypes = this.$store.state.contractStore.itemTypes
    const seen = new Map<string, any>()
    for (const assetUuid of this.allAssetUuids) {
      const asset = storeItems[assetUuid]
      if (!asset) continue
      const model = itemTypes[asset.modelCode]
      if (!model?.properties) continue
      const props = Array.isArray(model.properties) ? model.properties : Object.values(model.properties)
      for (const pm of props as any[]) {
        // Exclure uniquement les propriétés internes (système)
        if (pm.internal === true) continue
        const fmt = pm.type?.format || pm.type?.code || ''
        if (fmt.match(/OBJECT/)) continue
        if (!seen.has(pm.code)) {
          seen.set(pm.code, {code: pm.code, label: pm.name || pm.code, type: pm.type, choices: pm.choices || []})
        }
      }
    }
    return Array.from(seen.values())
  }

  // Résout la valeur (slug) de la catégorie de pack d'un asset, quel que soit le code de
  // la propriété : clé fixe 'packCategory', propriété de TYPE PACK_CATEGORY (code custom),
  // puis héritage depuis le parent isPackApplier. Source unique pour validation/tarification.
  getAssetPackCategoryValue(asset: any): string | null {
    if (!asset) return null
    if (asset.properties?.packCategory) return asset.properties.packCategory
    const model = this.$store.state.contractStore.itemTypes[asset.modelCode]
    if (model?.properties) {
      const props = Array.isArray(model.properties) ? model.properties : Object.values(model.properties)
      const pm: any = props.find((p: any) => p.type?.code === 'PACK_CATEGORY')
      if (pm && asset.properties?.[pm.code]) return asset.properties[pm.code]
    }
    // Fallback : hériter du parent (isPackApplier)
    const parent = asset.parentItem ? this.$store.state.contractStore.items[asset.parentItem] : null
    return parent ? this.getAssetPackCategoryValue(parent) : null
  }

  getAssetPackCategoryLabel(assetUuid: string): string {
    const storeItems = this.$store.state.contractStore.items
    const itemTypes = this.$store.state.contractStore.itemTypes
    const asset = storeItems[assetUuid]
    if (!asset) return ''
    // Trouver le code de la propriété PACK_CATEGORY déclarée sur l'itemType de l'asset
    const model = itemTypes[asset.modelCode]
    let packCode: string | null = null
    if (model?.properties) {
      const props = Array.isArray(model.properties) ? model.properties : Object.values(model.properties)
      const pm: any = props.find((p: any) => p.type?.code === 'PACK_CATEGORY' || p.code === 'packCategory')
      packCode = pm?.code || null
    }
    // Fallback : code 'packCategory' (legacy / hash dynamique)
    const value = packCode ? asset.properties?.[packCode] : asset.properties?.packCategory
    if (!value) return ''
    const slug = String(value).toUpperCase()
    const cats = this.$store.state.contractStore.packCategories
    const arr = Array.isArray(cats) ? cats : Object.values(cats || {})
    const cat: any = arr.find((c: any) => c.slug?.toUpperCase() === slug)
    return cat?.label || value
  }

  formatAssetPropertyValue(value: any, col: {type: any, choices: any[]}): string {
    if (helper.empty(value)) return '—'
    const code = col.type?.code
    if (code === 'BOOLEAN') return value === true || value === 'true' || value === '1' ? '✓' : '✗'
    if (code === 'RADIO' || code === 'CHECKBOX') {
      const choice: any = (col.choices || []).find((c: any) => c.code === value || c.code === String(value).toUpperCase())
      return choice?.label || value
    }
    if (code === 'PACK_CATEGORY') {
      const cat = this.$store.state.contractStore.packCategories?.find((c: any) => c.slug?.toUpperCase() === String(value).toUpperCase()) ||
                  (Object.values(this.$store.state.contractStore.packCategories || {}) as any[]).find((c: any) => c.slug?.toUpperCase() === String(value).toUpperCase())
      return cat?.label || value
    }
    return String(value)
  }

  get selectedInsurerGuarantees(): any[] {
    const ins = this.$store.state.contractStore.insurer
    if (!ins) return []
    const packs = this.$store.state.contractStore.packs || []
    const insurerPacks = packs.filter((p: any) =>
      p.insurer?.slug === ins.slug || String(p.insurer?.id) === String(ins.id)
    )
    const seen = new Map<number, any>()
    for (const p of insurerPacks) {
      for (const pg of (p.packGuarantees || [])) {
        const gId = pg.guarantee?.id
        if (!gId || seen.has(gId)) continue
        seen.set(gId, {
          id: gId,
          label: pg.guarantee?.label,
          deductible: pg.deductible,
          ceiling: pg.ceiling,
        })
      }
    }
    return Array.from(seen.values())
  }

  getAssetGuaranteeInfo(ac: any, guaranteeId: number): any | null {
    const g = ac.guarantees.find((g: any) => g.id === guaranteeId)
    return g || null
  }

  get assetCoverages(): any[] {
    const storeItems = this.$store.state.contractStore.items
    const packs = this.$store.state.contractStore.packs || []
    const ins = this.$store.state.contractStore.insurer
    if (!ins) return []
    const insurerPacks = packs.filter((p: any) =>
      p.insurer?.slug === ins.slug || String(p.insurer?.id) === String(ins.id)
    )
    const result: any[] = []
    for (const assetUuid of this.allAssetUuids) {
      const asset = storeItems[assetUuid]
      if (!asset) continue
      const covUuids = asset.properties?.couverture || []
      if (covUuids.length === 0) continue
      const covItem = storeItems[covUuids[0]]
      if (!covItem) continue
      const category = covItem.properties?.categorie
      const pack = insurerPacks.find((p: any) => p.category?.slug?.toUpperCase() === category?.toUpperCase())
      if (!pack) continue
      // Construire la liste des garanties avec leur état actif
      const activeIds = new Set((covItem.properties?.risques || []).map((r: any) => r.guarantee))
      const guarantees: any[] = []
      for (const pg of (pack.packGuarantees || [])) {
        const gId = pg.guarantee?.id
        if (!gId) continue
        guarantees.push({
          id: gId,
          label: pg.guarantee?.label,
          isOptional: pg.isOptional || false,
          active: activeIds.has(gId),
          premium: this.selectedOptionalPremiums[gId] || 0,
          deductible: pg.deductible,
          ceiling: pg.ceiling,
        })
      }
      result.push({
        assetUuid,
        assetName: getIdentifier(asset) || asset.name || asset.properties?.label || category,
        covUuid: covUuids[0],
        packLabel: pack.label,
        guarantees,
      })
    }
    return result
  }

  toggleGuarantee(ac: any, g: any) {
    const storeItems = this.$store.state.contractStore.items
    const covItem = storeItems[ac.covUuid]
    if (!covItem) return
    const risques = covItem.properties?.risques || []
    if (g.active) {
      // Retirer la garantie
      const filtered = risques.filter((r: any) => r.guarantee !== g.id)
      Vue.set(covItem.properties, 'risques', filtered)
    } else {
      // Ajouter la garantie
      risques.push({
        guarantee: g.id,
        label: g.label,
        premium: null,
        min: g.deductible,
        max: g.ceiling,
      })
      Vue.set(covItem.properties, 'risques', [...risques])
    }
    // Mettre à jour aussi optionalGuarantees de l'asset pour le moteur
    const asset = storeItems[ac.assetUuid]
    if (asset) {
      const optG = asset.properties?.optionalGuarantees || []
      if (g.active) {
        // Retirer de optionalGuarantees
        Vue.set(asset.properties, 'optionalGuarantees', optG.filter((id: number) => id !== g.id))
      } else {
        // Ajouter à optionalGuarantees
        if (!optG.includes(g.id)) {
          Vue.set(asset.properties, 'optionalGuarantees', [...optG, g.id])
        }
      }
    }
    this.prepareCalculate()
  }

  getGuaranteeStatus(cr: any, guaranteeId: number): string {
    const packs = this.$store.state.contractStore.packs || []
    const ins = cr.insurer
    const insurerPacks = packs.filter((p: any) =>
      p.insurer?.slug === ins.slug || String(p.insurer?.id) === String(ins.id)
    )
    for (const p of insurerPacks) {
      for (const pg of (p.packGuarantees || [])) {
        if (pg.guarantee?.id === guaranteeId) {
          return pg.isOptional ? 'optional' : 'included'
        }
      }
    }
    return 'none'
  }

  get comparisonDetailRows(): string[] {
    // Ne garder que les details qui correspondent à un premiumDetailModel
    const pdmCodes = new Set<string>()
    this.premiumDetails.forEach((pd: any) => {
      const code = typeof pd.model.method === 'string' ? pd.model.method : pd.model.method?.code || pd.model.code
      if (code) pdmCodes.add(code)
    })
    const codes: string[] = []
    for (const cr of this.validComparisonResults) {
      for (const d of (cr.details || [])) {
        if (d.code && pdmCodes.has(d.code) && !codes.includes(d.code)) {
          codes.push(d.code)
        }
      }
    }
    return codes
  }

  getComparisonDetail(cr: any, code: string): any {
    return (cr.details || []).find((d: any) => d.code === code)
  }

  getComparisonDetailName(code: string): string {
    for (const cr of this.comparisonResults) {
      const d = (cr.details || []).find((d: any) => d.code === code)
      if (d?.name) return d.name
    }
    return code
  }

  get flatDebugDetails(): any[] {
    const flat: any[] = []
    const walk = (list: any[], depth: number, parentAlias?: string) => {
      for (const d of list) {
        // Les lignes forEach (method+alias sans name) sont des headers redondants
        if (!d.name && d.method && d.details) {
          for (const child of d.details) {
            child._depth = depth
            child._parentAlias = d.alias || d.item
            child._hasChildren = !!(child.details && child.details.length > 0)
            flat.push(child)
            if (child._hasChildren && this.debugExpanded.has(child)) {
              walk(child.details, depth + 1)
            }
          }
        } else {
          d._depth = depth
          d._parentAlias = parentAlias
          d._hasChildren = !!(d.details && d.details.length > 0)
          flat.push(d)
          if (d._hasChildren && this.debugExpanded.has(d)) {
            walk(d.details, depth + 1)
          }
        }
      }
    }
    walk(this.debugDetails, 0)
    return flat
  }

  toggleDebugExpand(detail: any) {
    if (this.debugExpanded.has(detail)) {
      this.debugExpanded.delete(detail)
    } else {
      this.debugExpanded.add(detail)
    }
    // Force reactivity since Set isn't reactive in Vue 2
    this.debugExpanded = new Set(this.debugExpanded)
  }

  get requiredItems() {
    return this.$store.getters["contractStore/requiredItems"]
  }

  get searchProperties() {
    if (!helper.empty(this.itemSearch.item)) {
      return this.$store.getters["contractStore/getEntityTypeProperties"](this.itemSearch.item)
    }
  }

  getItemTypeProperties(modelCode: string) {
    const itemType = this.$store.state.contractStore.itemTypes[modelCode]
    return itemType?.properties || {}
  }

  isNumeric(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(value)
  }

  canAddItemType(itemType: any): boolean {
    const avenantType = this.$store.state.contractStore.avenantType
    if (!avenantType) return true
    if (!avenantType.allowAdd) return false
    if (avenantType.allowedAddition?.length > 0) {
      return avenantType.allowedAddition.includes(itemType.code)
    }
    return true
  }

  async generateAvenant() {
    const result = this.result
    for (let item of Object.values(this.$store.state.contractStore.items) as any) {
      if (!item.isValid) {
        validateItem(item)
      }
      if (!item.isValid) {
        Swal.fire({
          title: 'Erreur',
          html: "L'objet " + getIdentifier(item) + " est incomplet",
          timer: 10000,
          timerProgressBar: true,
          showCancelButton: true,
          confirmButtonText: 'OK',
        })
        return
      }
    }

    if (!result) {
      new Popup('Erreur', 'Veuillez sélectionner une offre')
      return
    }

    const total = this.deadlines.reduce((sum: number, deadline: any) => sum + (parseFloat(deadline.premium) || 0), 0);
    const expectedPremium = parseFloat(result.customPremium ?? result.premium) || 0
    if (this.deadlines.length > 0 && Math.abs(total - expectedPremium) > 1) {
      new Popup('Erreur', "La somme des montants échéances est différent du montant de l'avenant");
      return
    }

    const {items, required, assetUuids, itemUuid} = this.buildMapperData()

    // Construire la map required: {modelCode: uuid}
    const requiredMap: any = {}
    for (const uuid of required) {
      const item = items[uuid]
      if (item) requiredMap[item.modelCode] = uuid
    }
    // S'assurer que racine est dans required
    if (itemUuid && items[itemUuid]) {
      requiredMap[items[itemUuid].modelCode] = itemUuid
    }

    // Construire premiumDetails au format attendu: {pdmId: {premium, label, ...}}
    const pdMap: any = {}
    this.premiumDetails.forEach((pd: any) => {
      pdMap[String(pd.model.id)] = {
        premium: pd.isCustomized ? pd.customPremium : pd.premium,
        label: pd.model.label,
        operator: pd.model.operator,
      }
    })

    // Construire commissions au format attendu: {commissionModelId: {premium}}
    const commMap: any = {}
    this.commissions.forEach((c: any) => {
      commMap[String(c.model.id)] = {premium: c.premium}
    })

    // Deep copier les items pour ne pas muter le store
    const orderItems: any = JSON.parse(JSON.stringify(items))

    // Filet de sécurité : pour tout enfant qui n'aurait pas parentItem/parentPropertyCode posés à la création
    // (anciens flows, items chargés d'un avenant existant, etc.), on les déduit depuis les propriétés du parent.
    // Lie aussi asset ↔ customer via beneficiary (comme Mapper.php).
    const itemTypes = this.$store.state.contractStore.itemTypes
    for (const [parentUuid, parentItem] of Object.entries(orderItems) as any[]) {
      if (!parentItem.properties) continue
      const parentModel = itemTypes[parentItem.modelCode]
      for (const [propCode, propValue] of Object.entries(parentItem.properties) as any[]) {
        if (Array.isArray(propValue)) {
          const propertyModel: any = parentModel?.properties?.[propCode]
          for (const childUuid of propValue) {
            const childItem = orderItems[childUuid]
            if (childItem) {
              if (!childItem.parentItem) childItem.parentItem = parentUuid
              if (!childItem.parentPropertyCode) childItem.parentPropertyCode = propCode
            }
            if (childItem && parentModel && propertyModel) {
              const childModel = itemTypes[childItem.modelCode]
              if (parentModel.className === 'Asset' && childModel?.className === 'Customer') {
                parentItem.beneficiary = {uuid: childUuid}
              } else if (parentModel.className === 'Customer' && childModel?.className === 'Asset') {
                childItem.beneficiary = {uuid: parentUuid}
              }
            }
          }
        }
      }
    }

    // Index asset → coverage UUID pour retrouver les optionalGuarantees
    const assetByCoverage: any = {}
    for (const [uuid, item] of Object.entries(orderItems) as any[]) {
      if (item.properties?.couverture && Array.isArray(item.properties.couverture)) {
        for (const covUuid of item.properties.couverture) {
          assetByCoverage[covUuid] = uuid
        }
      }
    }

    // Convertir les coverages : risques en objet, nettoyer les details/inlineDetails
    for (const [uuid, item] of Object.entries(orderItems) as any[]) {
      if (item.modelCode === 'coverage') {
        // Convertir risques array → objet indexé par guaranteeId
        if (Array.isArray(item.properties?.risques)) {
          const risquesMap: any = {}
          const assetUuid = assetByCoverage[uuid]
          const optG = assetUuid ? (orderItems[assetUuid]?.properties?.optionalGuarantees || []) : []
          item.properties.risques.forEach((r: any) => {
            if (r.guarantee == null) return
            risquesMap[String(r.guarantee)] = {
              label: r.label,
              min: r.min ?? null,
              max: r.max ?? null,
              isOptional: optG.includes(r.guarantee),
              premium: r.premium ?? null,
            }
          })
          item.properties.risques = risquesMap
        }
        // Remonter les détails au niveau item — le backend lit $data['details']
        // (AvenantGenerator.php:733, identique à ce que fait Calculator::setAssetsPremiums)
        if (Array.isArray(item.properties.details)) {
          item.details = item.properties.details
        }
        // En mono-actif, le pdMap global = les premiumDetails de l'unique coverage.
        // Le backend (AvenantGenerator.php:801-825) l'utilise pour créer les rows premium_detail
        // rattachées à la coverage. Deep clone obligatoire : sinon Vue.prototype.stringify
        // (main.ts circular-replacer) détecte la ref partagée avec order.premiumDetails et
        // omet silencieusement la deuxième occurrence → backend reçoit un payload sans
        // premiumDetails au niveau root et throw "Propriété premiumDetails manquante".
        if (assetUuids.length === 1) {
          item.premiumDetails = JSON.parse(JSON.stringify(pdMap))
        }
        delete item.properties.details
      }
    }

    // Construire reductions au format attendu: {reductionModelId: {premium}}
    const redMap: any = {}
    this.reductions.forEach((r: any) => {
      redMap[String(r.model.id)] = {premium: r.premium}
    })

    // Résoudre startAt depuis la propriété de l'avenant avec link='startAt'
    let startAt = this.start
    if (!startAt) {
      const avenantUuid = requiredMap['avenant']
      const avenantItem = orderItems[avenantUuid]
      if (avenantItem) {
        const avenantModel = this.$store.state.contractStore.itemTypes[avenantItem.modelCode]
        if (avenantModel?.properties) {
          const props = Array.isArray(avenantModel.properties) ? avenantModel.properties : Object.values(avenantModel.properties)
          for (const pm of props as any[]) {
            if (pm.link === 'startAt' && avenantItem.properties[pm.code]) {
              startAt = avenantItem.properties[pm.code]
              break
            }
          }
        }
      }
    }

    const order: any = {
      entities: {
        items: orderItems,
        required: requiredMap,
        assetUuids,
      },
      branch: this.$route.params.branchSlug,
      avenantType: this.$store.state.contractStore.avenantType.code,
      contract: this.contractUuid,
      avenant: this.avenantuuid,
      insurer: this.$store.state.contractStore.insurer.slug,
      premium: result.premium,
      customPremium: result.customPremium,
      startAt: startAt,
      premiumDetails: pdMap,
      deadlines: this.deadlines,
      commissions: commMap,
      reductions: redMap,
    }
    this.lastOrder = order
    await this.submitOrder(order)
  }

  async submitOrder(order: any) {
    const res = await api.post(api.core, 'avenant/generator', order)
    this.$store.commit('stopLoading')
    if (!res || !res.data) return
    const data = res.data
    if (data.status === 'validation_bypassable') {
      this.handleBypassable(data)
      return
    }
    if (data.uuid) {
      this.$router.push({name: 'contract-show', params: {contractUuid: data.uuid}})
    } else if (data.message) {
      Swal.fire({title: 'Erreur', html: data.message, icon: 'error'})
    }
  }

  handleBypassable(data: any) {
    this.bypassableErrors = data.errors || []
    if (!data.canBypass) {
      const html = this.bypassableErrors
        .map((e: any) => {
          const head = e.itemLabel ? `${e.itemTypeName} — ${e.itemLabel}` : (e.itemTypeName || '')
          return `<b>${head}</b> · <i>${e.propertyName}</i><br/><small>${e.reason}</small>`
        })
        .join('<hr/>')
      Swal.fire({title: 'Propriétés invalides', html, icon: 'error'})
      return
    }
    this.bypassSelections = {}
    this.bypassReasons = {}
    for (const err of this.bypassableErrors) {
      const key = err.itemUuid + '|' + err.propertyCode
      this.$set(this.bypassSelections, key, false)
      this.$set(this.bypassReasons, key, '')
    }
    this.showBypassModal = true
  }

  async confirmBypass() {
    const overrides: any[] = []
    for (const err of this.bypassableErrors) {
      const key = err.itemUuid + '|' + err.propertyCode
      if (this.bypassSelections[key]) {
        overrides.push({
          itemUuid: err.itemUuid,
          propertyCode: err.propertyCode,
          reason: (this.bypassReasons[key] || '').trim() || null,
        })
      }
    }
    if (overrides.length === 0) {
      new Popup('Info', 'Aucune propriété sélectionnée à forcer')
      return
    }
    if (overrides.length !== this.bypassableErrors.length) {
      new Popup('Erreur', 'Toutes les propriétés invalides doivent être forcées pour créer le contrat')
      return
    }
    this.showBypassModal = false
    this.$store.commit('startLoading')
    const order = {...this.lastOrder, validationOverrides: overrides}
    this.lastOrder = order
    await this.submitOrder(order)
  }

  cancelBypass() {
    this.showBypassModal = false
    this.bypassableErrors = []
    this.bypassSelections = {}
    this.bypassReasons = {}
  }

  updateDuree(item: any) {
    if (getItemModel(item)?.code === 'avenant') {
      const itemType = this.$store.state.contractStore.itemTypes[item.modelCode]
      if (!itemType?.properties) return
      const propertyModels = Array.isArray(itemType.properties) ? itemType.properties : Object.values(itemType.properties)
      for (let pm of propertyModels as any[]) {
        const value = item.properties[pm.code]
        if (pm.link === 'duree' && !helper.empty(value)) {
          const choice: any = Object.values(pm.choices || {}).find((c: any) => {
            return c.code === value
          })
          this.durationCode = value
          this.duration = choice ? choice.label : ''
          this.updateEnd()
        }
        if (pm.link === 'dateEffet' && !helper.empty(value)) {
          if (helper.isValidDate(value)) {
            this.start = value
            this.updateEnd()
          } else {
            new Popup('Erreur', 'Date de debut invalide')
          }
        }
      }
    }
  }

  updateEnd() {
    const duration: Duration = this.durations.find((duration: Duration) => {
      return duration.code === this.durationCode
    }) as any
    if (this.start && this.duration && duration) {

      var endDate = new Date(this.start);
      if (duration.year > 0) {
        endDate.setFullYear(endDate.getFullYear() + duration.year)
      }
      if (duration.month > 0) {
        endDate.setMonth(endDate.getMonth() + duration.month)
      }
      if (duration.day > 0) {
        endDate.setDate(endDate.getDate() + duration.day)
      }
      endDate.setSeconds(endDate.getSeconds() - 1)
      this.end = endDate
    }
  }

  async mounted() {

    if (this.$route.params.contractUuid) {
      this.contractUuid = this.$route.params.contractUuid
    }
    if (this.$route.params.avenantuuid) {
      this.avenantuuid = this.$route.params.avenantuuid
    }
    //TODO Implementer les restrictions par AvenantType (branches,assureurs,retraits,impacte prime,creation contract)
    await this.$store.dispatch('masterStore/loadDataTypes')
    await this.loadData()
    await this.loadEngine()
  }

  downloadData() {
    this.$store.commit('loading')
    const storeItems = this.$store.state.contractStore.items
    const flatItems: any = {}
    for (const [uuid, item] of Object.entries(storeItems) as any[]) {
      if (item.modelCode === 'coverage') continue
      const flatProps: any = {}
      for (const [code, prop] of Object.entries(item.properties) as any[]) {
        flatProps[code] = prop && typeof prop === 'object' && 'value' in prop ? prop.value : prop
      }
      flatItems[uuid] = {modelCode: item.modelCode, name: item.name, properties: flatProps}
    }
    const blob = new Blob([JSON.stringify(flatItems, null, 2)], {type: 'application/json'});
    let url = window.URL || window.webkitURL;
    let link = url.createObjectURL(blob);
    let a = document.createElement('a');
    const name = 'AN_' + this.$route.params.branchSlug + '_' + helper.generateId() + '_' + helper.generateId() + '.devis';
    a.setAttribute("download", name);
    a.setAttribute("href", link);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    this.$store.commit('stopLoading')
  }

  async loadJsonData(e: any) {
    const file = e.target.files[0]
    var reader = new FileReader();
    let data: any = null;
    this.$store.commit('loading')
    reader.onload = async (evt: any) => {

      this.$store.commit('stopLoading')

      // const data = JSON.parse(evt.target.result)
      // const test: any = {}
      // data.entities.forEach((entity: any) => {
      //   test[entity.uuid] = entity
      // })
      // return

      this.$store.state.contractStore.currentItem = null
      // const requiredItems = this.$store.getters["contractStore/requiredItems"]

      let rootUuid = ''
      const items: any = JSON.parse(evt.target.result)

      const itemTypes = this.$store.state.contractStore.itemTypes
      store.commit('contractStore/setEntity', {type: 'items', data: {}})
      for (const [uuid, raw] of Object.entries(items) as any[]) {
        raw.uuid = uuid
        if (!raw.errors) raw.errors = []
        if (raw.isValid === undefined) raw.isValid = false
        if (raw.coverage === undefined) raw.coverage = null
        const model = itemTypes[raw.modelCode]
        if (!model) {
          // Ignorer les items coverage orphelins
          if (raw.modelCode === 'coverage') continue
          store.commit('contractStore/addEntity', {type: 'items', item: raw})
          continue
        }
        if (model.isRoot) rootUuid = uuid
        // Si ancien format (objets Property avec .value), extraire les valeurs plates
        const flatProps: any = {}
        for (const [code, entry] of Object.entries(raw.properties) as any[]) {
          flatProps[code] = entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry ? entry.value : entry
        }
        // Compléter les propriétés manquantes depuis le modèle
        const modelProps = Array.isArray(model.properties) ? model.properties : Object.values(model.properties || {})
        for (const pm of modelProps as any[]) {
          if (!(pm.code in flatProps)) {
            const typeCode = pm.type?.code || pm.type?.format || ''
            flatProps[pm.code] = typeCode.match(/OBJECT/) ? [] : (pm.value ?? null)
          }
        }
        raw.properties = flatProps
        store.commit('contractStore/addEntity', {type: 'items', item: raw})
      }

      // Set parentItem sur chaque item importé en walking la hiérarchie
      // Sans ça, getAlias avec aliasFromParent ne fonctionne pas tant que le AvenantItemTree de chaque sous-item n'a pas monté (et set parentItem dans sa mounted)
      const allItems = this.$store.state.contractStore.items
      for (const [parentUuid, parent] of Object.entries(allItems) as any[]) {
        const parentModel = itemTypes[parent.modelCode]
        if (!parentModel?.properties) continue
        const props = Array.isArray(parentModel.properties) ? parentModel.properties : Object.values(parentModel.properties)
        for (const pm of props as any[]) {
          const fmt = pm.type?.format || pm.type?.code || ''
          if (!fmt.match(/OBJECT/)) continue
          const val = parent.properties?.[pm.code]
          const childUuids = Array.isArray(val) ? val : (val ? [val] : [])
          childUuids.forEach((childUuid: string) => {
            const child = allItems[childUuid]
            if (child) {
              Vue.set(child, 'parentItem', parentUuid)
              Vue.set(child, 'parentProperty', pm.uuid || pm.code)
            }
          })
        }
      }

      // items.forEach((i:any)=>{
      //   i.validate()
      // })
      this.$store.commit('stopLoading')
      this.$store.state.contractStore.currentItem = rootUuid
      this.$store.commit('contractStore/setEntity', {type: 'rootUuid', data: rootUuid})

      // Valider tous les items importés et résoudre l'insurer depuis les couvertures
      const packs = this.$store.state.contractStore.packs || []
      for (const item of Object.values(this.$store.state.contractStore.items) as any[]) {
        if (item.modelCode === 'coverage') continue
        checkItemPropertiesVisibilities(item)
        validateItem(item)
        this.updateDuree(item)
        // Résoudre l'insurer depuis la couverture
        if (!this.$store.state.contractStore.insurer) {
          const covUuids = item.properties?.couverture
          if (Array.isArray(covUuids) && covUuids.length > 0) {
            const covItem = this.$store.state.contractStore.items[covUuids[0]]
            if (covItem?.properties?.pack) {
              const pack = packs.find((p: any) => p.code?.toUpperCase() === covItem.properties.pack?.toUpperCase())
              if (pack?.insurer) {
                this.$store.state.contractStore.insurer = pack.insurer
              }
            }
          }
        }
      }

      this.prepareCalculate()

    }
    reader.readAsText(file, "UTF-8");

  }

  updateItems(uuids: any) {
    uuids.forEach((uuid: string) => {
      if (!this.toBeValidated.includes(uuid)) {
        this.toBeValidated.push(uuid)
      }
    })

    if (this.toBeValidated.length > 0) {
      this.validateItems()
    }

    // Re-déclencher la comparaison en mode realtime si déjà comparé une fois (debounced)
    this.scheduleComparisonRefresh()
  }

  validateItems() {
    let requirementUpdated = false
    if (!helper.empty(this.toBeValidated)) {
      const uuids = [...new Set(this.toBeValidated)]
      this.toBeValidated = []
      const items = this.$store.getters["contractStore/getEntitiesByUuid"]('items', uuids)
      items.forEach((i: any) => {
        checkItemPropertiesVisibilities(i)
        validateItem(i)
        this.updateDuree(i)
        if (getItemModel(i)?.required && i.isValid) {
          requirementUpdated = true
        }
      })
    }
    if (requirementUpdated) {
      for (let uuid of this.$store.state.contractStore.assets) {
        if (this.$store.state.contractStore.items.hasOwnProperty(uuid)) {
          Vue.set(this.$store.state.contractStore.items[uuid], 'calculationKey', helper.generateId())
        }
      }
      this.prepareCalculate()
    } else {
      this.prepareCalculate()
    }
  }

  prepareCalculate() {
    Vue.set(this, 'calculating', true)
    clearTimeout(this.calculateTimeout)
    this.calculateTimeout = setTimeout(() => {
      clearTimeout(this.calculateTimeout)
      this.calculate()
    }, 1000)
  }

  onItemRemoved(uuid: string) {
    this.prepareCalculate()
    this.scheduleComparisonRefresh()
  }

  comparisonRefreshTimer: any = null

  scheduleComparisonRefresh() {
    // Pas de re-compare si l'utilisateur n'a jamais cliqué "Comparer"
    if (this.comparisonResults.length === 0) return
    // Pas de re-compare si déjà en train de comparer
    if (this.comparing) return
    clearTimeout(this.comparisonRefreshTimer)
    this.comparisonRefreshTimer = setTimeout(() => {
      clearTimeout(this.comparisonRefreshTimer)
      this.compareAllInsurers()
    }, 800)
  }

  async compareAllInsurers() {
    this.comparing = true
    this.comparisonResults = []

    if (!engineService.isEngineLoaded) {
      await this.loadEngine()
    }
    if (!engineService.isEngineLoaded) {
      this.comparisonResults = [{insurer: {label: 'Erreur'}, premium: 0, error: 'Moteur non chargé (engineService.isEngineLoaded=false)', details: [], errors: []}]
      this.comparing = false
      return
    }

    const itemTypes = this.$store.state.contractStore.itemTypes
    const insurerMap = this.$store.state.contractStore.insurers || {}
    const {items, required, assetUuids, itemUuid} = this.buildMapperData()
    const {packCategories, optionalMap} = this.resolveAssetConfigs(items, assetUuids)

    // Filtrer les assureurs qui ont au moins un pack dans les catégories demandées
    const packs = this.$store.state.contractStore.packs || []
    const insurerIds = Object.keys(insurerMap).filter((iid: string) => {
      const ins = insurerMap[iid]
      if (!ins || !ins.packs || ins.packs.length === 0) return false
      if (packCategories.length === 0) return true
      return packs.some((p: any) =>
        (p.insurer?.id === ins.id || p.insurer?.slug === ins.slug) &&
        packCategories.some((cat: string) => p.category?.slug?.toUpperCase() === cat?.toUpperCase())
      )
    })
    if (insurerIds.length === 0) {
      this.comparisonResults = [{insurer: {label: 'Aucun résultat'}, premium: 0, error: 'Aucun assureur avec des packs dans les catégories: ' + (packCategories.join(', ') || 'aucune catégorie configurée'), details: [], errors: []}]
      this.comparing = false
      return
    }

    let methodCode = this.$store.state.contractStore.avenantType?.calculationFunction?.code
    if (!methodCode) methodCode = typeof this.$store.state.contractStore.avenantType?.calculationFunction === 'string' ? this.$store.state.contractStore.avenantType.calculationFunction : null
    if (!methodCode) {
      this.comparisonResults = [{insurer: {label: 'Erreur'}, premium: 0, error: 'calculationFunction introuvable sur avenantType: ' + JSON.stringify(this.$store.state.contractStore.avenantType?.calculationFunction), details: [], errors: []}]
      this.comparing = false
      return
    }

    // Récupérer les IDs de réduction activés
    const reductionModels = this.$store.state.contractStore.reductionModels || []
    const toArray = (v: any) => !v ? [] : Array.isArray(v) ? v : Object.values(v)
    const reductionIds = toArray(reductionModels).filter((rm: any) => rm.isEnable).map((rm: any) => rm.id)

    const payload = {
      required,
      itemUuid,
      assetUuids,
      method: methodCode,
      debug: true,
      // Audit ligne-par-ligne RESERVE ROLE_MANAGER : trace la ligne en cours avant chaque exécution
      // pour pointer précisément celle qui plante quand une méthode throw (sinon « rien ne s'affiche »).
      audit: !!auth.hasRole('ROLE_MANAGER'),
      reductions: reductionIds,
    }

    // Snapshot des données réellement envoyées au moteur (bouton « Données envoyées au moteur »).
    this.engineInput = {insurers: insurerIds.map((id: string) => insurerMap[id]?.label || id), itemUuid, items, payload}

    await engineService.setStaticData(itemTypes, insurerMap)
    await engineService.setItems(items)
    // Laisser les workers recevoir les buffers avant de lancer les calculs
    await new Promise(r => setTimeout(r, 100))

    const results: any[] = []
    try {
      await engineService.calculatePerInsurer(
        insurerIds,
        payload,
        (result: any) => {
          const insurer = insurerMap[result.insurerId]
          let reductionAmount = 0
          if (result.reductions) {
            Object.values(result.reductions).forEach((r: any) => { reductionAmount += (typeof r === 'number' ? r : 0) })
          }
          const entry: any = {
            insurer: insurer || {label: result.insurer || ('Assureur #' + result.insurerId), slug: result.insurerId},
            premium: result.premium || 0,
            reductionAmount,
            details: result.details || [],
            errors: result.errors || [],
            error: result.error || null,
            // Stack du moteur (nomme la fonction generee + la ligne) et snapshot des objets envoyes
            // au moteur (item, coverages/pack, methode) : remontes par le worker pour rendre une
            // erreur « Cannot read properties of undefined (reading 'pack') » diagnosticable.
            errorStack: result.errorStack || null,
            errorContext: result.errorContext || null,
            assetResults: result.assetResults || {},
            optionalPremiums: result.optionalPremiums || {},
          }
          results.push(entry)
          // Mise à jour progressive
          this.comparisonResults = [...results].sort((a, b) => {
            if (a.error && !b.error) return 1
            if (!a.error && b.error) return -1
            return a.premium - b.premium
          })
        }
      )
    } catch (e: any) {
      if (results.length === 0) {
        results.push({insurer: {label: 'Erreur'}, premium: 0, error: e.message, details: [], errors: []})
      }
    }

    results.sort((a, b) => {
      if (a.error && !b.error) return 1
      if (!a.error && b.error) return -1
      return a.premium - b.premium
    })
    this.comparisonResults = results
    this.comparing = false
  }

  selectInsurer(cr: any) {
    this.$store.state.contractStore.insurer = cr.insurer
    this.selectedOptionalPremiums = cr.optionalPremiums || {}
    this.result = {
      uuid: helper.generateId(),
      premium: cr.premium,
      details: cr.details,
      insurer: cr.insurer,
    }
    this.debugDetails = cr.details || []

    // D'abord propager le packCategory des parents isPackApplier à tous les enfants assets
    const storeItems = this.$store.state.contractStore.items
    const itemTypes = this.$store.state.contractStore.itemTypes
    const propagate = (parentUuid: string) => {
      const parent = storeItems[parentUuid]
      if (!parent) return
      const model = itemTypes[parent.modelCode]
      if (!model?.properties) return
      const props = Array.isArray(model.properties) ? model.properties : Object.values(model.properties || {})
      for (const pm of props as any[]) {
        const fmt = pm.type?.format || pm.type?.code || ''
        if (!fmt.match(/OBJECT/)) continue
        const val = parent.properties?.[pm.code]
        const children = Array.isArray(val) ? val : (val ? [val] : [])
        for (const childUuid of children) {
          const child = storeItems[childUuid]
          if (!child) continue
          const childModel = itemTypes[child.modelCode]
          // Si le parent a une catégorie de pack (code fixe ou custom) et l'enfant est un Asset
          // sans catégorie → propager. On résout via getAssetPackCategoryValue pour gérer les codes custom.
          const parentCat = this.getAssetPackCategoryValue(parent)
          if (parentCat && childModel?.className === 'Asset' && !this.getAssetPackCategoryValue(child)) {
            Vue.set(child.properties, 'packCategory', parentCat)
            if (!child.properties.requiredGuarantees?.length) Vue.set(child.properties, 'requiredGuarantees', [...(parent.properties.requiredGuarantees || [])])
            if (!child.properties.optionalGuarantees?.length) Vue.set(child.properties, 'optionalGuarantees', [...(parent.properties.optionalGuarantees || [])])
          }
          propagate(childUuid)
        }
      }
    }
    const rootUuid = this.$store.state.contractStore.currentItem
    if (rootUuid) propagate(rootUuid)

    // Phase 1 : valider tous les assets AVANT de créer quoi que ce soit
    const packs = this.$store.state.contractStore.packs || []
    const insurerPacks = packs.filter((p: any) => p.insurer?.slug === cr.insurer.slug || p.insurer?.id === cr.insurer.id)
    const coveragePlan: any[] = []

    for (const assetUuid of this.allAssetUuids) {
      const asset = storeItems[assetUuid]
      if (!asset) continue

      const category = this.getAssetPackCategoryValue(asset)
      const reqG: number[] = asset.properties?.requiredGuarantees || []
      const optG: number[] = asset.properties?.optionalGuarantees || []

      if (!category) {
        Swal.fire({title: 'Configuration incomplète', html: asset.name + " n'a pas de catégorie de pack configurée.", icon: 'warning'})
        return
      }

      let pack = null
      for (const p of insurerPacks) {
        if (p.category?.slug?.toUpperCase() !== category?.toUpperCase()) continue
        const pgIds = (p.packGuarantees || []).map((pg: any) => pg.guarantee?.id)
        if (reqG.every((gId: number) => pgIds.includes(gId))) {
          pack = p
          break
        }
      }
      if (!pack) {
        Swal.fire({title: 'Assureur incompatible', html: cr.insurer.label + " n'a pas d'offre compatible pour " + asset.name + " (" + category + ").", icon: 'warning'})
        return
      }

      const risques: any[] = []
      if (pack.packGuarantees) {
        pack.packGuarantees.forEach((pg: any) => {
          if (!pg.isOptional || reqG.includes(pg.guarantee?.id) || optG.includes(pg.guarantee?.id)) {
            risques.push({
              guarantee: pg.guarantee?.id,
              label: pg.guarantee?.label,
              premium: null,
              min: pg.deductible,
              max: pg.ceiling,
            })
          }
        })
      }
      coveragePlan.push({assetUuid, asset, pack, risques, category})
    }

    // Phase 2 : tous les assets sont validés — créer les coverages
    for (const plan of coveragePlan) {
      const {assetUuid, asset, pack, risques, category} = plan
      const covUuid = helper.generateId()
      const covItem: any = {
        uuid: covUuid,
        modelCode: 'coverage',
        name: 'Couverture ' + (asset.name || ''),
        errors: [],
        isValid: true,
        coverage: null,
        properties: {
          label: pack.label,
          pack: pack.code?.toUpperCase(),
          categorie: (pack.category?.slug || category)?.toUpperCase(),
          risques,
          premium: 0,
          details: [],
        }
      }
      store.commit('contractStore/addEntity', {type: 'items', item: covItem})
      Vue.set(asset.properties, 'couverture', [covUuid])
    }

    // Distribuer les details par asset depuis les résultats de comparaison
    this.distributeCoverageDetails(cr)
    this.prepareCalculate()
    // Avancer à l'étape suivante uniquement après que toutes les validations soient passées
    this.currentStep = 2
  }

  showComparisonDebug(cr: any) {
    const label = cr.insurer?.label || ''
    this.debugDetails = cr.details || []
    this.debugLogs = cr.errors?.map((e: any) => {
      const ea = e.audit || cr.errorContext?.audit
      return {type: 'error', msg: e.message || e, where: ea ? (label + ' → ' + (ea.method || '?') + ' (Ligne ' + ea.line + (ea.type ? ', ' + ea.type : '') + ')') : '', link: this.methodLink(ea), data: (e.stack || ea || cr.errorContext) ? {audit: ea || null, trace: e.auditTrace || cr.errorContext?.auditTrace || null, stack: e.stack || cr.errorStack || null, input: cr.errorContext || null} : null}
    }) || []
    if (cr.error && !this.debugLogs.length) {
      const a = cr.errorContext?.audit
      const where = a ? (label + ' → ' + (a.method || '?') + ' (Ligne ' + a.line + (a.type ? ', ' + a.type : '') + ')')
        : (cr.errorStack ? label + ' → ' + (String(cr.errorStack).split('\n').find((l: string) => /\bat\s+/.test(l)) || '').trim() : label)
      this.debugLogs = [{type: 'error', msg: cr.error, where, link: this.methodLink(a), data: {audit: a || null, trace: cr.errorContext?.auditTrace || null, stack: cr.errorStack || null, input: cr.errorContext || null}}]
    }
    this.debugFunctionDetail = null
    this.debugExpanded = new Set()
    this.showDebug = true
  }

  // Route vers l'éditeur de la fonction moteur (engine-method-editor) à partir de la ligne auditée.
  // null si l'audit n'a pas d'id/version (mode audit off / flow non régénéré) -> pas de lien affiché.
  methodLink(a: any) {
    if (!a || !a.methodId || !a.engineVersion) return null
    return {
      name: 'engine-method-editor',
      params: {
        engineVersion: String(a.engineVersion),
        branchSlug: String(this.$route.params.branchSlug || ''),
        methodId: String(a.methodId),
        methodSlug: a.methodCode || a.code || '_',
      },
    }
  }

  // Vrai uniquement pour le nœud synthétique de secours (trace ✓/✗ : segments avec label/code).
  // Les nœuds d'erreur RICHES (rapportés par le try/catch de la méthode) ont de vrais inlineDetails
  // (operation/expression/value) -> rendu normal.
  isTraceNode(d: any): boolean {
    return !!(d && d.inlineDetails && d.inlineDetails[0] && d.inlineDetails[0][0] && d.inlineDetails[0][0].label !== undefined)
  }

  // --- Affichage des conditions (if) : groupes OU, atomes ET, couleur selon résultat ---
  groupCond(line: any[]): any[][] {
    const g: any[][] = []
    for (const p of (line || [])) { const i = p.group || 0; (g[i] = g[i] || []).push(p) }
    return g.filter(Boolean)
  }
  // Atomes déterminants à montrer en compact : le groupe satisfait (tous vrais) si la ligne est vraie,
  // sinon les atomes non-vrais (false/undefined) qui bloquent.
  condAtoms(line: any[]): any[] {
    for (const grp of this.groupCond(line)) { if (grp.length && grp.every((p: any) => p.value === true)) return grp }
    return (line || []).filter((p: any) => p.value !== true)
  }
  lineResult(line: any[]): boolean | undefined {
    let undef = false
    for (const grp of this.groupCond(line)) {
      if (grp.length && grp.every((p: any) => p.value === true)) return true
      if (grp.some((p: any) => p.value === undefined)) undef = true
    }
    return undef ? undefined : false
  }
  condClass(p: any): string {
    return p.value === true ? 'badge-light-success' : p.value === false ? 'badge-light-danger' : 'badge-light-warning'
  }
  condIcon(p: any): string {
    return p.value === true ? 'fa-check' : p.value === false ? 'fa-xmark' : 'fa-question'
  }
  cmpSym(cmp: string): string {
    return cmp === '!==' ? '≠' : cmp === '>=' ? '≥' : cmp === '<=' ? '≤' : cmp === '===' ? '' : cmp
  }
  atomBig(p: any): string {
    const s = this.cmpSym(p.cmp || '===')
    const exp = (p.expected !== undefined && p.expected !== '') ? p.expected : p.expression
    return (s ? s + ' ' : '') + exp
  }
  atomExpected(p: any): string {
    return (p.expected !== undefined && p.expected !== '') ? String(p.expected) : (p.expression || '')
  }
  condActual(p: any): string {
    const v = p.actual
    if (v === undefined || v === null) return '(non défini)'
    if (typeof v === 'boolean') return v ? 'vrai' : 'faux'
    if (v === '') return '(vide)'
    return String(v)
  }

  showAllComparisonDebug() {
    const allDetails: any[] = []
    const allLogs: any[] = []
    for (const cr of this.comparisonResults) {
      if (cr.details?.length > 0) {
        allDetails.push({name: cr.insurer?.label || 'Assureur', value: cr.premium, code: 'insurer_' + cr.insurer?.id, details: cr.details, error: cr.error || null})
      }
      const label = cr.insurer?.label || ''
      if (cr.error) {
        // msg = texte de l'erreur SEUL. where = « assureur → méthode (Ligne N, type) » (sans le code).
        // link = route vers l'éditeur de la fonction (router-link, nouvel onglet). data = dump complet.
        const a = cr.errorContext?.audit
        const where = a ? (label + ' → ' + (a.method || '?') + ' (Ligne ' + a.line + (a.type ? ', ' + a.type : '') + ')')
          : (cr.errorStack ? label + ' → ' + (String(cr.errorStack).split('\n').find((l: string) => /\bat\s+/.test(l)) || '').trim() : label)
        allLogs.push({type: 'error', msg: cr.error, where, link: this.methodLink(a), data: {audit: a || null, trace: cr.errorContext?.auditTrace || null, stack: cr.errorStack || null, input: cr.errorContext || null}})
      }
      if (cr.errors?.length > 0) {
        cr.errors.forEach((e: any) => {
          const ea = e.audit || cr.errorContext?.audit
          const where = ea ? (label + ' → ' + (ea.method || '?') + ' (Ligne ' + ea.line + (ea.type ? ', ' + ea.type : '') + ')') : ''
          allLogs.push({type: 'error', msg: (e.message || e), where, link: this.methodLink(ea), data: (e.stack || ea || cr.errorContext) ? {audit: ea || null, trace: e.auditTrace || cr.errorContext?.auditTrace || null, stack: e.stack || cr.errorStack || null, input: cr.errorContext || null} : null})
        })
      }
    }
    this.debugDetails = allDetails
    this.debugLogs = allLogs
    this.debugFunctionDetail = null
    this.debugExpanded = new Set()
    this.showDebug = true
  }



  // Une etape est masquee quand le type d'avenant ne recalcule pas la prime :
  // Garanties (3), Details (4) et Echeancier (5) n'ont alors aucun sens.
  // Cas AFFAIRE_NOUVELLE / RENOUVELLEMENT (isUpdatingPremium != false) : tout reste actif.
  isStepEnabled(n: number): boolean {
    const at = this.$store.state.contractStore.avenantType
    if (at && at.isUpdatingPremium === false && (n === 3 || n === 4 || n === 5)) {
      return false
    }
    return true
  }

  get visibleSteps(): any[] {
    return this.wizardSteps.filter((s: any) => this.isStepEnabled(s.n))
  }

  get lastStep(): number {
    const v = this.visibleSteps
    return v.length ? v[v.length - 1].n : 6
  }

  get isFirstStep(): boolean {
    const v = this.visibleSteps
    return !v.length || this.currentStep <= v[0].n
  }

  goNext() {
    if (this.currentStep === 1 && !this.$store.state.contractStore.insurer) return
    const next = this.visibleSteps.find((s: any) => s.n > this.currentStep)
    if (next) this.currentStep = next.n
  }

  goPrev() {
    const prevs = this.visibleSteps.filter((s: any) => s.n < this.currentStep)
    if (prevs.length) this.currentStep = prevs[prevs.length - 1].n
  }

  goToStep(step: number) {
    if (!this.isStepEnabled(step)) return
    if (step === 1 || this.$store.state.contractStore.insurer) {
      this.currentStep = step
    }
  }

  printRecap() {
    // Collecter tout le contenu des pages devis (recap + pages detail par actif)
    const pages = document.querySelectorAll('.devis-page')
    if (!pages.length) return
    let content = ''
    pages.forEach(p => { content += '<div class="page">' + p.innerHTML + '</div>' })

    const printWindow = window.open('', '_blank')
    if (!printWindow) return
    printWindow.document.write(`<html><head>
      <title>Devis ${this.$store.state.contractStore.branch?.label || ''} - ${this.$store.state.contractStore.insurer?.label || ''}</title>
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;300;400;500;600;700;800&display=swap" rel="stylesheet">
      <style>
        *{box-sizing:border-box;font-size:0.97em}
        @page{size:A3}
        body{font-family:'Montserrat',sans-serif;margin:0;background:white}
        @media print{.no-print{display:none !important}}
        @media screen{body{background:whitesmoke;font-size:1.15em}.page{max-width:1240px;padding:2rem;margin:30px auto;background:white;box-shadow:0 0 5px gainsboro}}
        .page{display:flex;flex-direction:column;justify-content:flex-start;page-break-after:always;page-break-inside:avoid}
        table{border-collapse:collapse}
        td{padding:0 5px}
        .head{background-color:#F5F5F5;border-radius:10px;width:100%;text-align:center;padding:1rem}
        .head td span{text-align:left;display:inline-block}
        h2{margin:0 0 0.5rem 0;font-weight:300;font-size:1.5em;color:#f8069c}
        .carac .property{flex-grow:1;background-color:#f5f5f5;padding:0.5rem 1rem;margin:5px 3px 0 2px;white-space:nowrap}
        .table-striped>tbody>tr:nth-of-type(odd){background:#F5F5F5;color:#000}
      </style>
    </head><body>`)
    printWindow.document.write(content)
    printWindow.document.write('</body></html>')
    printWindow.document.close()
    printWindow.onload = () => { printWindow.print() }
  }

  getGuaranteeLabel(id: number): string {
    const all = this.$store.state.contractStore.guarantees
    const arr = Array.isArray(all) ? all : Object.values(all || {})
    const g = arr.find((g: any) => g.id === id)
    return g?.label || '#' + id
  }

  getItemModel(item: any) {
    return this.$store.state.contractStore.itemTypes?.[item?.modelCode]
  }

  getIdentifier(item: any) {
    return item ? getIdentifier(item) : ''
  }

  isPropertyVisible(pm: any, value: any, item?: any): boolean {
    if (!pm.type) return false
    const format = pm.type.format || pm.type.code || ''
    if (format.match(/OBJECT/)) return false
    if (pm.internal) return false
    if (value === null || value === undefined || value === '') return false
    if (value === 0 || value === '0') return false
    if (value === false || value === 'false') return false
    if (pm.showOnDevis === false) return false
    // Vérifier la visibilité conditionnelle (conditionGroups)
    if (item && pm.conditionGroups && Object.keys(pm.conditionGroups).length > 0) {
      if (!getPropertyVisibility(item, pm)) return false
    }
    return true
  }

  formatPropertyValue(pm: any, value: any): string {
    if (value === null || value === undefined || value === '') return ''
    const format = pm.type?.format || pm.type?.code || ''
    // Choices : chercher le label dans pm.choices
    if (pm.choices && Object.keys(pm.choices).length > 0) {
      const choices = Array.isArray(pm.choices) ? pm.choices : Object.values(pm.choices)
      const match = choices.find((c: any) => c.code === value || c.value === value || c.id === value)
      if (match) return match.label || match.name || value
    }
    if (format === 'AMOUNT') {
      const num = parseFloat(value)
      if (!isNaN(num)) return helper.currencyFormat(num)
    }
    if (format === 'NUMBER') {
      const num = parseFloat(value)
      if (!isNaN(num)) return num.toLocaleString('fr-FR')
    }
    if (format === 'DATE' && value) {
      try {
        const d = new Date(value)
        if (!isNaN(d.getTime())) return d.toLocaleDateString('fr-FR')
      } catch (e) {}
      return value
    }
    if (format === 'BOOLEAN') {
      return value === true || value === 'true' || value === 1 || value === '1' ? 'Oui' : 'Non'
    }
    return String(value)
  }

  isOptionalRisk(risk: any, assetUuid: string): boolean {
    const asset = this.$store.state.contractStore.items[assetUuid]
    if (!asset) return false
    const optG = asset.properties?.optionalGuarantees || []
    return optG.includes(risk.guarantee)
  }

  getAssetCoverage(assetUuid: string): any {
    const asset = this.$store.state.contractStore.items[assetUuid]
    if (!asset) return null
    const covUuids = asset.properties?.couverture
    if (!Array.isArray(covUuids) || covUuids.length === 0) return null
    return this.$store.state.contractStore.items[covUuids[0]] || null
  }

  startEditPremiumDetail(pd: any) {
    pd.customPremium = pd.isCustomized ? pd.customPremium : pd.premium
    pd.edit = true
  }

  saveEditPremiumDetail(pd: any) {
    pd.edit = false
    if (pd.customPremium !== pd.premium) {
      pd.isCustomized = true
      pd.originalPremium = pd.premium
      const user = this.$store.state.mainStore.user
      pd.modifiedBy = user ? ((user.prename || '') + ' ' + (user.name || '')).trim() || user.email : 'Agent'
      pd.modifiedAt = new Date().toLocaleDateString('fr-FR')
    } else {
      pd.isCustomized = false
      pd.originalPremium = 0
      pd.modifiedBy = ''
      pd.modifiedAt = ''
    }
    this.updateTotalPremium()
  }

  cancelEditPremiumDetail(pd: any) {
    pd.edit = false
  }

  updateTotalPremium() {
    if (!this.result) return
    let hasCustom = false
    let total = 0
    this.premiumDetails.forEach((pd: any) => {
      if (pd.isCustomized) {
        total += pd.customPremium
        hasCustom = true
      } else {
        total += pd.premium
      }
    })
    Vue.set(this.result, 'customPremium', hasCustom ? total : null)
  }

  expand(evt: any) {
    if (Array.isArray(evt)) {
      // evt.forEach((uuid: string) => {
      //   const item = this.$store.getters["contractStore/getEntity"]('items', uuid)
      //   if (item) {
      //   }
      // })
      this.$store.commit('contractStore/setItemsExpanded', evt)
    }
  }

  updateEditedItem() {
    validateItem(this.$store.state.contractStore.editedItem)
    if (!this.$store.state.contractStore.editedItem.isValid) {
      new Popup(this.$store.state.contractStore.editedItem.name, "L'objet contient des erreurs")
    }
    store.commit('contractStore/addEntity', {type: 'items', item: this.$store.state.contractStore.editedItem});
    store.commit('contractStore/setEntity', {type: 'editedItem', data: null});

  }

  async loadData() {
    this.loadDurations()

    // Charger l'avenantType séparément (dépend du type dans l'URL)
    const atRes = await api.get(api.core, 'contract/avenant/requirements/' + this.$route.params.avenantType + '/' + this.$route.params.branchSlug)
    if (atRes?.data) {
      store.commit('contractStore/setEntity', {type: 'avenantType', data: atRes.data.avenantType})
      store.commit('contractStore/setEntity', {type: 'branchCategory', data: atRes.data.branchCategory})
      if (atRes.data.commissionModels) {
        store.commit('contractStore/setEntity', {type: 'commissionModels', data: atRes.data.commissionModels})
      }
    }

    // Récupérer le branchId pour filtrer le sync par branche
    const branchId = atRes?.data?.branch?.id || null

    // Sync core : charge itemTypes, insurers, packs, etc. au format plat dénormalisé
    // Le syncCore reconstruit les relations (type résolu, choices, conditionGroups, packs rattachés aux insurers)
    await this.$store.dispatch('contractStore/syncCore', {
      branchSlug: this.$route.params.branchSlug,
      branchId: branchId,
    })

    // Trouver le branchId pour les futures syncs delta
    const branch = this.$store.state.contractStore.branch
    if (branch?.id) {
      this.branchId = branch.id
    }

    // Avenant de vie : précharger l'état réel du contrat existant (format plat fourni
    // par le backend ContractItemFlattener). Sinon (affaire nouvelle), items vierges.
    const loadedItems: Record<string, any> = {}
    let loadedRootUuid: string | null = null
    if (this.$route.params.contractUuid) {
      const ci = await api.get(api.core, 'contract/get/avenant/items/' + this.$route.params.contractUuid)
      if (ci?.data?.items) {
        Object.assign(loadedItems, ci.data.items)
        loadedRootUuid = ci.data.rootUuid || null
      }
    }

    // Créer les items required/root
    const itemTypes = this.$store.state.contractStore.itemTypes
    store.commit('contractStore/setEntity', {type: 'items', data: {}})

    // 1) Injecter les items existants chargés depuis le contrat.
    const presentModelCodes = new Set<string>()
    for (const item of Object.values(loadedItems) as any[]) {
      store.commit('contractStore/addEntity', {type: 'items', item})
      if (item.modelCode) presentModelCodes.add(item.modelCode)
    }
    if (loadedRootUuid) {
      store.commit('contractStore/setEntity', {type: 'currentItem', data: loadedRootUuid})
      store.commit('contractStore/setEntity', {type: 'rootUuid', data: loadedRootUuid})
    }

    // 2) Créer en blanc uniquement les required/root absents du contrat chargé
    //    (typiquement l'item 'avenant' du nouvel avenant).
    for (const [code, it] of Object.entries(itemTypes) as any[]) {
      if ((it.required || it.isRoot) && !presentModelCodes.has(it.code)) {
        const item: any = {
          uuid: helper.generateId(),
          modelCode: it.code,
          name: it.name,
          properties: {},
          errors: [],
          isValid: false,
          coverage: null,
        }
        const props = Array.isArray(it.properties) ? it.properties : Object.values(it.properties || {})
        props.forEach((pm: any) => {
          const typeCode = pm.type?.code || pm.type?.format || ''
          const isObj = typeCode.match(/OBJECT/)
          item.properties[pm.code] = isObj ? [] : (pm.value ?? null)
        })
        store.commit('contractStore/addEntity', {type: 'items', item})
        if (it.isRoot) {
          store.commit('contractStore/setEntity', {type: 'currentItem', data: item.uuid})
          store.commit('contractStore/setEntity', {type: 'rootUuid', data: item.uuid})
        }
      }
      // ASACI workflow
      try {
        const workflows = Array.isArray(it.workflows) ? it.workflows : (it.workflows ? JSON.parse(it.workflows) : [])
        workflows.forEach((w: any) => {
          if ((typeof w === 'string' ? w : w?.code) === 'asaci') {
            this.$store.dispatch('comparatorStore/loadAutoCertificates')
          }
        })
      } catch (e) {}
    }

    this.checkPremiumDetails()
    this.checkCommissions()
    this.checkReductions()
  }

  checkPremiumDetails() {
    this.$store.state.contractStore.premiumDetailModels.forEach((pdm: any) => {
      const match = this.premiumDetails.find((pd: PremiumDetail) => {
        return pd.model.id === pdm.id
      })
      if (!match) {
        const pd = new PremiumDetail()
        pd.model = pdm
        pd.premium = 0
        this.premiumDetails.push(pd)
      }
    })
  }

  checkCommissions() {
    this.$store.state.contractStore.commissionModels.forEach((cm: CommissionModel) => {

      if (cm.isEnable) {
        let match = this.commissions.find((c: Commission) => {
          return c.model.id === cm.id
        })
        if (!match) {
          match = new Commission()
          match.model = cm
          match.premium = 0
          this.commissions.push(match)
        }
        if (cm.method && cm.method.code) {
          this.commissionMap[cm.method.code] = match
        }
      }
    })
  }

  checkReductions() {
    this.$store.state.contractStore.reductionModels.forEach((reductionModel: ReductionModel) => {
      if (reductionModel.isEnable) {
        const match = this.reductions.find((c: Reduction) => {
          return c.model.id === reductionModel.id
        })
        if (!match) {
          const occ = new Reduction()
          occ.model = reductionModel
          occ.premium = 0
          this.reductions.push(occ)
        }
      }
    })
  }

  getReste() {
    if (this.result) {
      let premium = this.result.premium
      for (let deadline of this.deadlines) {
        if (!isNaN(deadline.premium)) {
          premium -= deadline.premium
        }
      }
      this.deadlineReste = premium
    }
    this.deadlineReste = 0
  }

  addDeadline() {
    let total = 0
    for (let deadline of this.deadlines) {
      if (helper.empty(deadline.date) || deadline.premium == 0) {
        new Popup('Erreur', 'Veuillez renseignez les échéances précédentes');
        return
      }
      if (!isNaN(deadline.premium)) {
        total += parseFloat(deadline.premium)
      }
    }
    if (this.result && this.deadlines.length > 0 && total >= this.result.premium) {
      new Popup('Erreur', "La somme des échéances est supérieur ou égal au montant de l'avenant");
      return
    }
    this.getReste()
    const deadline = {date: null, premium: this.deadlineReste}
    this.deadlines.push(deadline)
  }

  removeDeadline(index: any) {
    this.deadlines.splice(index, 1)
  }

  async loadDurations() {
    const res = await api.get(api.master, 'api/get/durations')
    if (res && res.data && res.data.durations) {
      this.durations = []
      res.data.durations.forEach((dur: Duration) => {
        this.durations.push(new Duration(dur))
      })
    }
  }

  async loadEngine() {
    this.engineStatus = 'loading'
    try {
      await api.loadWorkerEngine(this.$route.params!.branchSlug)
      this.engineStatus = engineService.isEngineLoaded ? 'ready' : 'error'
    } catch (e) {
      this.engineStatus = 'error'
    }
  }

  prepareChildrenLink() {
    clearTimeout(this.linking)
    this.linking = setTimeout(this.linkChildren, 50)
  }

  linkChildren() {

    const connectors = document.querySelectorAll('.connectors')
    connectors.forEach((c: any) => {
      const paths = c.querySelectorAll('path');
      paths.forEach((p: any) => p.remove());

    })
    const children: any = document.querySelectorAll(".draggable-item[data-parent]")
    children.forEach((child: any) => {
      const itemUuid = child.getAttribute('data-parent');
      const svg: any = document.querySelector('#connector_' + itemUuid)
      const parent: any = document.querySelector("#item_" + itemUuid);


      const parentRect = parent!.getBoundingClientRect();
      const svgRect = svg.getBoundingClientRect();
      const childRect = child.getBoundingClientRect();
      const endX = childRect.left + childRect.width / 2 - svgRect.left;
      const endY = childRect.top - svgRect.top;
      const childUuid = child.id.replace('item_', '')
      let path = document.querySelector("#connector_" + itemUuid + " #connect_" + childUuid)!
      let hiddenPath = document.querySelector("#connector_" + itemUuid + " #hidden_connect_" + childUuid)!

      const startX = parentRect.left + parentRect.width / 2 - svgRect.left;
      const startY = parentRect.bottom - svgRect.top;

      if (!path) {
        hiddenPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
        hiddenPath.setAttribute("id", "#hidden_connect_" + childUuid);
        hiddenPath.setAttribute("class", "path link pointer");
        svg.appendChild(hiddenPath);
        path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("id", "#connect_" + childUuid);
        path.setAttribute("class", "path link show");
        svg.appendChild(path);
      }

      if (path) {
        const delta1 = 0;
        const delta2 = 60;
        const delta3 = 0;
        const delta4 = 60;
        const controlX1 = startX + delta1;
        const controlY1 = startY + delta2;
        const controlX2 = endX - delta3;
        const controlY2 = endY - delta4;

        const d = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;
        path.setAttribute('d', d);
        // path.setAttribute('stroke', 'gainsboro');
        path.setAttribute('stroke-width', '2');
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke-dasharray', '5, 5');

        hiddenPath.setAttribute('d', d);
        hiddenPath.setAttribute('stroke', 'transparent');
        hiddenPath.setAttribute('stroke-width', '20');
        hiddenPath.setAttribute('fill', 'none');
      }
    })
  }

  @Watch('calculationEnd')
  updateCalculationTime() {
    const duration = this.calculationEnd - this.calculationStart; // en ms

    if (duration < 0 || isNaN(duration)) {
      this.calculationTime = '0 ms';
      return;
    }

    const parts = [];

    const ms = Math.round(duration % 1000);
    const totalSeconds = Math.floor(duration / 1000);
    const seconds = totalSeconds % 60;
    const minutes = Math.floor(totalSeconds / 60);

    if (minutes > 0) parts.push(`${minutes} min`);
    if (seconds > 0) parts.push(`${seconds} sec`);
    if (ms > 0 && duration < 10000) parts.push(`${ms} ms`); // Affiche les ms seulement si total < 10 sec

    this.calculationTime = parts.join(' ') || '0 ms';
  }

  resolveAssetConfigs(items: any, assetUuids: string[]) {
    const packCategories: string[] = []
    const optionalMap: any = {}
    const packs = this.$store.state.contractStore.packs || []
    for (const assetUuid of assetUuids) {
      const asset = items[assetUuid]
      if (!asset) continue
      const parent = asset.parentItem ? items[asset.parentItem] : null
      const category = this.getAssetPackCategoryValue(asset) || this.getAssetPackCategoryValue(parent)
      const optG = asset.properties?.optionalGuarantees?.length ? asset.properties.optionalGuarantees : (parent?.properties?.optionalGuarantees || [])
      if (category && !packCategories.includes(category)) packCategories.push(category)
      if (optG.length > 0) {
        packs.forEach((p: any) => {
          if (p.category?.slug?.toUpperCase() === category?.toUpperCase()) {
            optionalMap[p.id] = [...optG]
          }
        })
      }
    }
    return {packCategories, optionalMap}
  }

  buildMapperData() {
    const storeItems = this.$store.state.contractStore.items
    const itemTypes = this.$store.state.contractStore.itemTypes
    const items: any = {}
    const required: any[] = []
    const assetUuids: any[] = []
    let itemUuid: any = null

    Object.values(storeItems).forEach((storeItem: any) => {
      const model = itemTypes[storeItem.modelCode]
      if (!model) return
      // Les items sont déjà au format mapper { code: valeur }
      const props = {...storeItem.properties}
      // Deep copy arrays pour éviter les références partagées
      for (const [key, val] of Object.entries(props)) {
        if (Array.isArray(val)) {
          props[key] = [...val]
        }
      }
      // NORMALISATION catégorie de pack : la propriété peut avoir un code custom (ex: categorie_de_pack),
      // alors que la tarification (resolveAssetConfigs + worker) lit la clé fixe 'packCategory'. On la
      // pose ici depuis la propriété de TYPE PACK_CATEGORY -> le pack correspondant est trouvé et la
      // couverture injectée (sinon racine.vehicule.couverture reste vide -> .pack undefined).
      if (props.packCategory === undefined && model.properties) {
        const mprops = Array.isArray(model.properties) ? model.properties : Object.values(model.properties)
        const pm: any = (mprops as any[]).find((p: any) => p.type?.code === 'PACK_CATEGORY')
        if (pm && props[pm.code] !== undefined && props[pm.code] !== '' && props[pm.code] !== null) {
          props.packCategory = props[pm.code]
        }
      }
      items[storeItem.uuid] = {
        modelCode: storeItem.modelCode,
        name: storeItem.name,
        properties: props,
        parentItem: storeItem.parentItem || null,
        parentPropertyCode: storeItem.parentPropertyCode || null,
      }
      if (model.required || model.isRoot) {
        required.push(storeItem.uuid)
      }
      if (model.isRoot) {
        itemUuid = storeItem.uuid
      }
      if (model.className === 'Asset') {
        assetUuids.push(storeItem.uuid)
      }
    })
    return {items, required, assetUuids, itemUuid}
  }

  async calculate() {
    Vue.set(this, 'calculating', true)
    this.calculationStart = performance.now()
    this.result = null
    this.debugDetails = []

    if (!engineService.isEngineLoaded) {
      Vue.set(this, 'calculating', false)
      return
    }

    const currentInsurer = this.$store.state.contractStore.insurer
    if (!currentInsurer) {
      Vue.set(this, 'calculating', false)
      return
    }

    const itemTypes = this.$store.state.contractStore.itemTypes
    const insurerMap = this.$store.state.contractStore.insurers || {}
    const {items, required, assetUuids, itemUuid} = this.buildMapperData()

    let methodCode = this.$store.state.contractStore.avenantType?.calculationFunction?.code
    if (!methodCode) methodCode = typeof this.$store.state.contractStore.avenantType?.calculationFunction === 'string' ? this.$store.state.contractStore.avenantType.calculationFunction : null
    if (!methodCode) {
      Vue.set(this, 'calculating', false)
      return
    }

    const reductionModels = this.$store.state.contractStore.reductionModels || []
    const toArray = (v: any) => !v ? [] : Array.isArray(v) ? v : Object.values(v)
    const reductionIds = toArray(reductionModels).filter((rm: any) => rm.isEnable).map((rm: any) => rm.id)

    const payload = {
      required,
      itemUuid,
      assetUuids,
      method: methodCode,
      debug: true,
      // Audit ligne-par-ligne RESERVE ROLE_MANAGER : trace la ligne en cours avant chaque exécution
      // pour pointer précisément celle qui plante quand une méthode throw (sinon « rien ne s'affiche »).
      audit: !!auth.hasRole('ROLE_MANAGER'),
      reductions: reductionIds,
    }

    // Snapshot des données envoyées au moteur (bouton « Données envoyées au moteur »).
    this.engineInput = {insurers: [currentInsurer.label || currentInsurer.id], itemUuid, items, payload}

    await engineService.setStaticData(itemTypes, insurerMap)
    await engineService.setItems(items)

    let calcResult: any = null
    try {
      await engineService.calculatePerInsurer(
        [String(currentInsurer.id)],
        payload,
        (result: any) => {
          calcResult = result
        }
      )
    } catch (e: any) {
      // silencieux
    }

    if (calcResult) {
      this.debugDetails = calcResult.details || []
      // Mettre à jour les premiumDetails depuis les details du moteur
      const engineDetails = calcResult.details || []
      this.premiumDetails.forEach((pd: PremiumDetail) => {
        const code = typeof pd.model.method === 'string' ? pd.model.method : pd.model.method?.code || pd.model.code
        if (!code) return
        const match = engineDetails.find((d: any) => d.code === code)
        if (match && this.isNumeric(match.value)) {
          pd.premium = match.value
        }
      })

      // Mettre à jour les réductions depuis le résultat du moteur
      const engineReductions = calcResult.reductions || {}
      this.reductions.forEach((r: Reduction) => {
        const val = engineReductions[r.model.id]
        if (val !== undefined && this.isNumeric(val)) {
          r.premium = val
        }
      })

      this.result = {
        uuid: helper.generateId(),
        premium: calcResult.premium || 0,
        details: calcResult.details || [],
        insurer: currentInsurer
      }
      this.updateTotalPremium()
      // Source primaire des details par asset : AvenantItemTree::calculatePremium (écriture via syncCoverageToStore).
      // Backfill : si un asset n'a toujours pas de details après le calcul global (AvenantItemTree pas
      // encore re-tourné ou avant première sélection de pack), on comble depuis les données globales.
      // - Mono-actif : on copie les details globaux sur l'unique coverage.
      // - Multi-actifs : on parse l'arbre result.details pour trouver les sub.uuid matchant chaque asset.
      this.backfillMissingCoverageDetails(calcResult)
    }

    this.calculationEnd = performance.now()
    Vue.set(this, 'calculating', false)
  }

  /**
   * Distribue les primes et details du moteur vers les coverages de chaque asset.
   * Le moteur itère les items OBJECT_GROUP dans l'ordre de la propriété (array d'UUIDs).
   * On reconstruit ce même ordre pour mapper subDetail[i] → assetUuid[i].
   */
  // Comble les coverages qui n'ont pas encore de details (AvenantItemTree pas trigger
  // ou première passe avant sélection). Ne touche jamais une coverage déjà peuplée.
  backfillMissingCoverageDetails(result: any) {
    const storeItems = this.$store.state.contractStore.items
    const itemTypes = this.$store.state.contractStore.itemTypes
    const getCov = (assetUuid: string) => {
      const asset = storeItems[assetUuid]
      const covUuids = asset?.properties?.couverture
      if (!Array.isArray(covUuids) || covUuids.length === 0) return null
      return storeItems[covUuids[0]] || null
    }
    const isEmpty = (cov: any) => {
      const d = cov?.properties?.details
      return !Array.isArray(d) || d.length === 0
    }

    const assetUuids = this.allAssetUuids

    // Mono-actif : les details globaux sont ceux de l'unique coverage.
    if (assetUuids.length === 1) {
      const cov = getCov(assetUuids[0])
      if (cov && isEmpty(cov) && Array.isArray(result?.details) && result.details.length > 0) {
        Vue.set(cov.properties, 'details', result.details)
        if (result.premium > 0) Vue.set(cov.properties, 'premium', result.premium)
      }
      return
    }

    // Multi-actifs : parse l'arbre result.details pour trouver les sub.uuid par asset.
    const details = result?.details
    if (!Array.isArray(details)) return
    const collectByUuid = (list: any[]) => {
      for (const d of list) {
        if (!d.details || d.details.length === 0) continue
        for (const sub of d.details) {
          if (sub.uuid && storeItems[sub.uuid]) {
            const model = itemTypes[storeItems[sub.uuid].modelCode]
            if (model?.className === 'Asset') {
              const cov = getCov(sub.uuid)
              if (cov && isEmpty(cov)) {
                let premium = 0
                for (const sd of (sub.details || [])) {
                  if (sd.value && this.isNumeric(sd.value) && sd.value > 0) premium = sd.value
                }
                Vue.set(cov.properties, 'details', sub.details || [])
                if (premium > 0) Vue.set(cov.properties, 'premium', premium)
              }
            }
          }
          if (sub.details && sub.details.length > 0) {
            collectByUuid([sub])
          }
        }
      }
    }
    collectByUuid(details)
  }

  // Utilisée uniquement depuis selectInsurer (flow comparateur multi-assureurs).
  // Le flow wizard laisse AvenantItemTree::calculatePremium écrire les details par asset.
  distributeCoverageDetails(result: any) {
    const storeItems = this.$store.state.contractStore.items
    const itemTypes = this.$store.state.contractStore.itemTypes

    // 1. assetResults populé (objet non vide)
    if (result?.assetResults && Object.keys(result.assetResults).length > 0) {
      for (const [assetUuid, ar] of Object.entries(result.assetResults) as any[]) {
        this.setCoverageFromResult(assetUuid, ar)
      }
      return
    }

    // 2. Fallback parse récursif des sub.uuid dans l'arbre result.details
    const details = result?.details || result
    if (!Array.isArray(details)) return
    const collectByUuid = (list: any[]) => {
      for (const d of list) {
        if (!d.details || d.details.length === 0) continue
        for (const sub of d.details) {
          if (sub.uuid && storeItems[sub.uuid]) {
            const model = itemTypes[storeItems[sub.uuid].modelCode]
            if (model?.className === 'Asset') {
              let premium = 0
              for (const sd of (sub.details || [])) {
                if (sd.value && this.isNumeric(sd.value) && sd.value > 0) premium = sd.value
              }
              this.setCoverageFromResult(sub.uuid, {premium, details: sub.details || []})
            }
          }
          if (sub.details && sub.details.length > 0) {
            collectByUuid([sub])
          }
        }
      }
    }
    collectByUuid(details)
  }

  setCoverageFromResult(assetUuid: string, ar: any) {
    const storeItems = this.$store.state.contractStore.items
    const asset = storeItems[assetUuid]
    if (!asset) return
    const covUuids = asset.properties?.couverture
    if (!Array.isArray(covUuids) || covUuids.length === 0) return
    const cov = storeItems[covUuids[0]]
    if (!cov) return
    Vue.set(cov.properties, 'details', ar.details || [])
    if (ar.premium > 0) {
      Vue.set(cov.properties, 'premium', ar.premium)
    }
  }


}
</script>


<style scoped>
.devis-table-striped tbody tr:nth-child(even) {
  background: #f5f5f5;
}
.filter {
  position: relative;
  /*top: 0;*/
  /*left: 0;*/
  width: 100%;
  height: 100%;
  /* background: rgba(255, 255, 255, .75); */
  /*z-index: 9999;*/
  display: flex;
  justify-content: center;
  align-items: center;

  background-size: 4px 4px;
  background-image: radial-gradient(transparent 1px, rgba(255, 255, 255, .5) 1px);
  backdrop-filter: saturate(50%) blur(4px);
  /*backdrop-filter: blur(2px);*/
}

.debug-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
}

.debug-drawer-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 0;
}

.debug-drawer-content {
  position: relative;
  z-index: 1;
  min-width: 400px;
  max-width: 90vw;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease;
}

.slide-right-enter-active .debug-drawer-content,
.slide-right-leave-active .debug-drawer-content {
  transition: transform 0.3s ease;
}

.slide-right-enter .debug-drawer-content,
.slide-right-leave-to .debug-drawer-content {
  transform: translateX(100%);
}

.slide-right-enter .debug-drawer-overlay,
.slide-right-leave-to .debug-drawer-overlay {
  opacity: 0;
}

.slide-right-enter-active .debug-drawer-overlay,
.slide-right-leave-active .debug-drawer-overlay {
  transition: opacity 0.3s ease;
}

.checkbox-accordion {
  display: none;
}
.checkbox-accordion:checked ~ .accordion {
  display: block;
}
.checkbox-accordion:not(:checked) ~ .accordion {
  display: none;
}
.checkbox-accordion:checked ~ label .fa-caret-right {
  display: none;
}
.checkbox-accordion:not(:checked) ~ label .fa-caret-down {
  display: none;
}
.comparison-table tbody tr:nth-child(2n + 1) {
  background: rgba(61, 218, 215, 0.21);
}
.bypass-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.bypass-modal {
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.bypass-modal .card-body {
  overflow: auto;
}
.guarantee-scroll-wrapper {
  overflow-x: auto;
}
.guarantee-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}
.guarantee-label-col {
  position: sticky;
  left: 0;
  background-color: white;
  z-index: 2;
  min-width: 200px;
  border-right: 15px solid white;
  box-shadow: 5px 0 5px -3px rgba(0,0,0,0.08);
}
.guarantee-pack-col {
  position: sticky;
  left: 200px;
  background-color: white;
  z-index: 2;
  min-width: 140px;
  border-right: 15px solid white;
  box-shadow: 5px 0 5px -3px rgba(0,0,0,0.08);
}
.guarantee-table tbody tr:nth-child(2n + 1) td {
  background-color: #d4f4f3;
}
.guarantee-table tbody tr:nth-child(2n + 1) .guarantee-label-col,
.guarantee-table tbody tr:nth-child(2n + 1) .guarantee-pack-col {
  background-color: #d4f4f3;
}
.guarantee-table th:not(.guarantee-label-col) {
  border-right: 5px white solid;
}
.guarantee-table td:not(.guarantee-label-col) {
  border-right: 5px white solid;
}
.comparison-scroll-wrapper {
  position: relative;
}
.comparison-table {
  border-collapse: separate;
  border-spacing: 0;
}
.comparison-label-col {
  position: sticky;
  left: 0;
  z-index: 2;
  white-space: nowrap;
  background-color: white;
  border-right: 15px solid white;
  box-shadow: 5px 0 5px -3px rgba(0,0,0,0.08);
}
.comparison-table tbody tr:nth-child(2n + 1) .comparison-label-col {
  background-color: #d4f4f3;
}
.comparison-table tbody tr td:not(.comparison-label-col) {
  background-color: white;
}
.comparison-table tbody tr:nth-child(2n + 1) td:not(.comparison-label-col) {
  background-color: #d4f4f3;
}
.comparison-table td:not(.comparison-label-col),
.comparison-table th:not(.comparison-label-col) {
  border-right: 10px white solid;
}
.devis-actions {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  padding: 15px 20px;
  margin: 0 auto 10px auto;
  max-width: 1240px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  border-radius: 0 0 10px 10px;
}
.devis-page {
  display: flex;
  flex-direction: column;
  background: white;
  max-width: 1240px;
  padding: 2rem;
  margin: 30px auto;
  min-height: 1754px;
  box-shadow: 0 0 5px gainsboro;
  font-family: 'Montserrat', sans-serif;
}
.devis-head {
  background-color: #F5F5F5;
  border-radius: 10px;
  width: 100%;
  padding: 1rem;
}
.devis-head td span {
  text-align: left;
  display: inline-block;
}
/* Survol d'une condition : la vraie valeur (expression2) glisse du bas vers le haut. */
.expression-wrapper {
  position: relative;
  width: max-content;
  overflow: visible;
}
.expression1 {
  position: relative;
  z-index: 1;
  transition: opacity 0.25s ease;
}
.expression2 {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.25s ease, transform 0.25s ease;
  pointer-events: none;
  white-space: nowrap;
}
.expression-wrapper:hover .expression2 {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}
.expression-wrapper:hover .expression1 {
  opacity: 0;
}
/* Condition : double-clic pour basculer valeurs <-> champ/expression (la face arrière glisse). */
.cond-atom {
  position: relative;
  width: max-content;
  display: inline-block;
}
.cond-bottom {
  position: relative;
  display: block;
  overflow: visible;
  white-space: nowrap;
}
.cond-field,
.cond-actual {
  display: block;
  transition: transform 0.2s ease, opacity 0.2s ease;
  white-space: nowrap;
}
.cond-actual {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  transform: translateY(100%);
}
.cond-atom.flipped .cond-field {
  opacity: 0;
  transform: translateY(-100%);
}
.cond-atom.flipped .cond-actual {
  opacity: 1;
  transform: translateY(0);
}
</style>
